import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useEmpresaActiva } from '../../context/EmpresaContableContext';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, MenuItem,
  Button, Alert, CircularProgress, Autocomplete, Tabs, Tab, Chip, Divider, Grid, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { TabRegistro } from './RegistrosContables';
import { exportStyledXlsx } from './excelExportUtils';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

function exportPdf(title, subtitle, headers, rows, filename, landscape = false) {
  const doc = new jsPDF({ orientation: landscape ? 'landscape' : 'portrait' });
  doc.setFontSize(13); doc.text(title, 14, 15);
  doc.setFontSize(9);  doc.text(subtitle, 14, 22);
  autoTable(doc, { startY: 28, head: [headers], body: rows,
    styles: { fontSize: 8 }, headStyles: { fillColor: [25, 118, 210] } });
  doc.save(filename);
}

const MESES = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

function fmtPeriodo(p) {
  if (!p || p.length < 6) return p;
  return `${MESES[parseInt(p.slice(4,6))-1]} ${p.slice(0,4)}`;
}

function fmtNum(v) {
  const n = parseFloat(v || 0);
  if (n === 0) return '';
  return n.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtNumSaldo(v) {
  const n = parseFloat(v || 0);
  return n.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtFecha(s) {
  if (!s) return '';
  const [y, m, d] = String(s).substring(0, 10).split('-');
  return (y && m && d) ? `${d}/${m}/${y}` : String(s).substring(0, 10);
}

export default function ReportesContables({ onIrAAsiento } = {}) {
  const { empresaActivaId, setEmpresaActiva } = useEmpresaActiva();
  const [tab, setTab] = useState(0);
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState(empresaActivaId || '');
  const [periodos, setPeriodos] = useState([]);

  useEffect(() => {
    axios.get('/api/contabilidad/empresas')
      .then(r => {
        const todas = Array.isArray(r.data) ? r.data : [];
        setEmpresas(todas);
        const activas = todas.filter(e => e.activo);
        if (activas.length === 1) setEmpresaId(String(activas[0].id));
      }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!empresaId) { setPeriodos([]); return; }
    axios.get('/api/contabilidad/periodos', { params: { empresa_id: empresaId } })
      .then(r => setPeriodos(Array.isArray(r.data) ? r.data.sort((a, b) => a.periodo.localeCompare(b.periodo)) : []))
      .catch(() => {});
  }, [empresaId]);

  const empresaSel = empresas.find(e => String(e.id) === empresaId);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          <AssessmentIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h5" fontWeight="bold">Reportes Contables</Typography>
        </Box>
        <TextField select label="Empresa" size="small" value={empresaId}
          onChange={e => { setEmpresaId(e.target.value); setEmpresaActiva(e.target.value); }}
          sx={{ minWidth: { xs: '100%', sm: 260 }, ml: { sm: 'auto' } }}>
          {empresas.map(e => (
            <MenuItem key={e.id} value={String(e.id)}>
              {e.razon_social}{!e.activo && <span style={{ color: '#999', marginLeft: 6 }}>(Inactiva)</span>}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {!empresaId ? (
        <Alert severity="info">Seleccione una empresa contable para generar reportes.</Alert>
      ) : (
        <>
          <Paper elevation={0} variant="outlined" sx={{ mb: 3 }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}
              variant="scrollable" scrollButtons="auto">
              <Tab label="Balance de Comprobación" />
              <Tab label="Libro Diario" />
              <Tab label="Libro Mayor" />
              <Tab label="Estado de Resultados" icon={<TrendingUpIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
              <Tab label="Balance General" icon={<AccountBalanceIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
              <Tab label="Reg. Compras" icon={<ShoppingCartIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
              <Tab label="Reg. Ventas" icon={<PointOfSaleIcon sx={{ fontSize: 16 }} />} iconPosition="start" sx={{ color: tab === 6 ? 'success.main' : undefined }} />
              <Tab label="Caja y Bancos" icon={<AccountBalanceWalletIcon sx={{ fontSize: 16 }} />} iconPosition="start" sx={{ color: tab === 7 ? 'warning.dark' : undefined }} />
              <Tab label="Ratios Financieros" icon={<AssessmentIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
              <Tab label="Análisis Vert./Horiz." icon={<TrendingUpIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
              <Tab label="Flujo de Caja Proy." icon={<AccountBalanceWalletIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
              <Tab label="Conciliación Tributaria" icon={<AssessmentIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
            </Tabs>
          </Paper>

          {tab === 0 && (
            <BalanceComprobacion
              empresaId={empresaId}
              empresa={empresaSel}
              periodos={periodos}
              onIrAAsiento={onIrAAsiento}
            />
          )}
          {tab === 1 && (
            <LibroDiario
              empresaId={empresaId}
              empresa={empresaSel}
              periodos={periodos}
            />
          )}
          {tab === 2 && (
            <LibroMayor
              empresaId={empresaId}
              empresa={empresaSel}
              periodos={periodos}
            />
          )}
          {tab === 3 && (
            <EstadoResultados
              empresaId={empresaId}
              empresa={empresaSel}
              periodos={periodos}
            />
          )}
          {tab === 4 && (
            <BalanceGeneral
              empresaId={empresaId}
              empresa={empresaSel}
              periodos={periodos}
            />
          )}
          {tab === 5 && (
            <TabRegistro key={`co-${empresaId}`} tipo="CO"
              empresaId={empresaId} empresa={empresaSel} periodos={periodos} />
          )}
          {tab === 6 && (
            <TabRegistro key={`ve-${empresaId}`} tipo="VE"
              empresaId={empresaId} empresa={empresaSel} periodos={periodos} />
          )}
          {tab === 7 && (
            <LibroCajaBancos
              empresaId={empresaId} empresa={empresaSel} periodos={periodos} />
          )}
          {tab === 8 && (
            <RatiosFinancieros
              empresaId={empresaId} empresa={empresaSel} periodos={periodos} />
          )}
          {tab === 9 && (
            <AnalisisVerticalHorizontal
              empresaId={empresaId} empresa={empresaSel} periodos={periodos} />
          )}
          {tab === 10 && (
            <FlujoCajaProyectado
              empresaId={empresaId} empresa={empresaSel} />
          )}
          {tab === 11 && (
            <ConciliacionTributaria
              empresaId={empresaId} empresa={empresaSel} />
          )}
        </>
      )}
    </Box>
  );
}

// ─── Balance de Comprobación ────────────────────────────────────────────────
// Formato jerárquico estilo SUNAT "Formato 3.17: Libro de Inventarios y Balances —
// Balance de Comprobación": Saldos Iniciales / Movimientos / Saldos Finales por cuenta,
// con subtotales por cuenta mayor (2 dígitos) y subcuenta (3 dígitos), y clasificación en
// Balance General (Activo/Pasivo) y Estado de Resultados (Pérdida/Ganancia, Cargo/Abono).

const NIVELES_BALANCE = [
  { value: 'todos', label: 'Todos', hint: 'Todos los niveles' },
  { value: 'todos_auxiliar', label: 'Todos con Auxiliar', hint: 'Todos los niveles + cliente/proveedor' },
  { value: '2', label: '2 Dígitos', hint: 'Cuenta mayor' },
  { value: '3', label: '3 Dígitos', hint: 'Intermedia' },
  { value: 'analitica', label: 'Analítica', hint: 'Solo cuenta final' },
  { value: 'auxiliar', label: 'Auxiliar', hint: 'Cuenta final + cliente/proveedor' },
];

function BalanceComprobacion({ empresaId, empresa, periodos, onIrAAsiento }) {
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [nivelDetalle, setNivelDetalle] = useState('todos');
  const [moneda, setMoneda] = useState('PEN');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cuadreOpen, setCuadreOpen] = useState(false);
  const [cuadreLoading, setCuadreLoading] = useState(false);
  const [cuadreData, setCuadreData] = useState(null);
  const [cuadreErr, setCuadreErr] = useState('');

  const buscar = async () => {
    if (!desde || !hasta) { setError('Seleccione período desde y hasta'); return; }
    setLoading(true); setError('');
    try {
      const r = await axios.get('/api/contabilidad/reportes/balance-comprobacion-jerarquico', {
        params: { empresa_id: empresaId, periodo_desde: desde, periodo_hasta: hasta, nivel: nivelDetalle, moneda }
      });
      setRows(Array.isArray(r.data?.rows) ? r.data.rows : []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
    setLoading(false);
  };

  const validarCuadre = async () => {
    setCuadreOpen(true); setCuadreLoading(true); setCuadreErr(''); setCuadreData(null);
    try {
      const r = await axios.get('/api/contabilidad/reportes/validar-cuadre', {
        params: { empresa_id: empresaId, periodo_desde: desde || undefined, periodo_hasta: hasta || undefined }
      });
      setCuadreData(r.data);
    } catch (e) {
      setCuadreErr(e.response?.data?.error || e.message);
    }
    setCuadreLoading(false);
  };

  const hojas = rows.filter(r => r.nivel === 3);
  const totalDebeMov = hojas.reduce((s, r) => s + (r.debe_mov || 0), 0);
  const totalHaberMov = hojas.reduce((s, r) => s + (r.haber_mov || 0), 0);

  const nivelSx = (r) => {
    if (r.fila_especial === 'total') return { bgcolor: '#0d47a1', '&:hover': { bgcolor: '#0d47a1' }, '& td': { fontWeight: 'bold', color: 'white', borderTop: '2px solid #0d47a1' } };
    if (r.fila_especial === 'resultado') return { bgcolor: '#fff3cd', '&:hover': { bgcolor: '#fff3cd' }, '& td': { fontWeight: 'bold' } };
    if (r.fila_especial === 'subtotal') return { bgcolor: '#d7e7fb', '&:hover': { bgcolor: '#d7e7fb' }, '& td': { fontWeight: 'bold', borderTop: '2px solid #90caf9' } };
    if (r.nivel === 1) return { bgcolor: '#d7e7fb', '&:hover': { bgcolor: '#d7e7fb' }, '& td': { fontWeight: 'bold' } };
    if (r.nivel === 2) return { bgcolor: '#eef4fc', '&:hover': { bgcolor: '#eef4fc' }, '& td': { fontWeight: 'bold' } };
    if (r.nivel === 4) return { '& td': { fontStyle: 'italic', color: 'text.secondary' } };
    return {};
  };

  const num = v => v ? fmtNumSaldo(v) : '';
  const THIN = { borderLeft: '1px solid rgba(255,255,255,.35)' };
  const THIN_BODY = { borderLeft: '1px solid #e0e0e0' };

  const exportarExcel = () => {
    const columns = [
      { header: 'Código', key: '_codigo', width: 14 },
      { header: 'Cuenta', key: '_desc', width: 38 },
      { header: 'SD Inicial', key: '_sdIni', width: 14, type: 'number', totKey: 'sdIni' },
      { header: 'SA Inicial', key: '_saIni', width: 14, type: 'number', totKey: 'saIni' },
      { header: 'Debe Mov.', key: '_debeMov', width: 14, type: 'number', totKey: 'debeMov' },
      { header: 'Haber Mov.', key: '_haberMov', width: 14, type: 'number', totKey: 'haberMov' },
      { header: 'SD Final', key: '_sdFin', width: 14, type: 'number', totKey: 'sdFin' },
      { header: 'SA Final', key: '_saFin', width: 14, type: 'number', totKey: 'saFin' },
      { header: 'Activo', key: '_activo', width: 14, type: 'number', totKey: 'activo' },
      { header: 'Pasivo', key: '_pasivo', width: 14, type: 'number', totKey: 'pasivo' },
      { header: 'Pérdida', key: '_perdida', width: 14, type: 'number', totKey: 'perdida' },
      { header: 'Ganancia', key: '_ganancia', width: 14, type: 'number', totKey: 'ganancia' },
      { header: 'Cargo', key: '_cargo', width: 14, type: 'number', totKey: 'cargo' },
      { header: 'Abono', key: '_abono', width: 14, type: 'number', totKey: 'abono' },
    ];
    const data = rows.map(r => ({
      _codigo: r.codigo, _desc: r.descripcion,
      _sdIni: r.sd_ini, _saIni: r.sa_ini, _debeMov: r.debe_mov, _haberMov: r.haber_mov,
      _sdFin: r.sd_fin, _saFin: r.sa_fin, _activo: r.activo, _pasivo: r.pasivo,
      _perdida: r.perdida, _ganancia: r.ganancia, _cargo: r.cargo, _abono: r.abono,
    }));
    const totals = ['sdIni','saIni','debeMov','haberMov','sdFin','saFin','activo','pasivo','perdida','ganancia','cargo','abono']
      .reduce((acc, k, i) => {
        const field = ['sd_ini','sa_ini','debe_mov','haber_mov','sd_fin','sa_fin','activo','pasivo','perdida','ganancia','cargo','abono'][i];
        acc[k] = hojas.reduce((s, r) => s + (r[field] || 0), 0);
        return acc;
      }, {});
    exportStyledXlsx({
      empresa, titulo: `FORMATO 3.17 — BALANCE DE COMPROBACIÓN (${moneda === 'USD' ? 'US$' : 'S/'})`, periodoLabel: `${fmtPeriodo(desde)} a ${fmtPeriodo(hasta)}`,
      columns, rows: data, totals,
      filename: `BalanceComprobacion_${desde}_${hasta}_${moneda}.xlsx`, sheetName: 'Balance Comprobación', landscape: true,
    });
  };

  return (
    <Box>
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField select label="Período desde" size="small" value={desde}
            onChange={e => setDesde(e.target.value)} sx={{ minWidth: 180 }}>
            {periodos.map(p => (
              <MenuItem key={p.periodo} value={p.periodo}>{fmtPeriodo(p.periodo)}</MenuItem>
            ))}
          </TextField>
          <TextField select label="Período hasta" size="small" value={hasta}
            onChange={e => setHasta(e.target.value)} sx={{ minWidth: 180 }}>
            {periodos.map(p => (
              <MenuItem key={p.periodo} value={p.periodo}>{fmtPeriodo(p.periodo)}</MenuItem>
            ))}
          </TextField>
          <TextField select label="Nivel de detalle" size="small" value={nivelDetalle}
            onChange={e => setNivelDetalle(e.target.value)} sx={{ minWidth: 200 }}>
            {NIVELES_BALANCE.map(n => (
              <MenuItem key={n.value} value={n.value}>{n.label} — {n.hint}</MenuItem>
            ))}
          </TextField>
          <TextField select label="Moneda" size="small" value={moneda}
            onChange={e => setMoneda(e.target.value)} sx={{ minWidth: 130 }}>
            <MenuItem value="PEN">S/ Soles</MenuItem>
            <MenuItem value="USD">$ Dólares</MenuItem>
          </TextField>
          <Button variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            onClick={buscar} disabled={loading}>
            Generar
          </Button>
          <Button variant="outlined" color="warning" startIcon={<WarningIcon />} onClick={validarCuadre} disabled={!empresaId}>
            Validar Cuadre
          </Button>
          {rows.length > 0 && (<>
            <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}>Imprimir</Button>
            <Button variant="outlined" color="success" startIcon={<DownloadIcon />} onClick={exportarExcel}>Excel</Button>
          </>)}
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {rows.length > 0 && (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" align="center">
              {empresa?.razon_social}
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              Formato 3.17 — Balance de Comprobación — Períodos {fmtPeriodo(desde)} a {fmtPeriodo(hasta)}
              {' — '}{NIVELES_BALANCE.find(n => n.value === nivelDetalle)?.label}
              {' — '}<Box component="span" sx={{ fontWeight: 'bold', color: moneda === 'USD' ? 'info.main' : 'text.secondary' }}>
                Expresado en {moneda === 'USD' ? 'Dólares (US$)' : 'Soles (S/)'}
              </Box>
            </Typography>
            {moneda === 'USD' && (
              <Typography variant="caption" color="text.secondary" align="center" display="block">
                Las líneas en soles se convierten con el tipo de cambio venta del día de cada asiento (o el más reciente anterior disponible).
              </Typography>
            )}
          </Box>
          <TableContainer component={Paper} elevation={2} sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1500 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} rowSpan={2}>Código</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} rowSpan={2}>Cuenta y Subcuenta</TableCell>
                  <TableCell align="center" colSpan={2} sx={{ color: 'white', fontWeight: 'bold', ...THIN }}>Saldos Iniciales</TableCell>
                  <TableCell align="center" colSpan={2} sx={{ color: 'white', fontWeight: 'bold', ...THIN }}>Movimientos</TableCell>
                  <TableCell align="center" colSpan={2} sx={{ color: 'white', fontWeight: 'bold', ...THIN }}>Saldos Finales</TableCell>
                  <TableCell align="center" colSpan={2} sx={{ color: 'white', fontWeight: 'bold', ...THIN }}>Balance General</TableCell>
                  <TableCell align="center" colSpan={2} sx={{ color: 'white', fontWeight: 'bold', ...THIN }}>Edo. Resultados x Función</TableCell>
                  <TableCell align="center" colSpan={2} sx={{ color: 'white', fontWeight: 'bold', ...THIN }}>Resultado x Naturaleza</TableCell>
                </TableRow>
                <TableRow sx={{ bgcolor: 'primary.dark' }}>
                  <TableCell align="right" sx={{ color: 'white', fontSize: 11 }}>Debe</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontSize: 11 }}>Haber</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontSize: 11, ...THIN }}>Debe</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontSize: 11 }}>Haber</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontSize: 11, ...THIN }}>Debe</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontSize: 11 }}>Haber</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontSize: 11, ...THIN }}>Activo</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontSize: 11 }}>Pasivo</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontSize: 11, ...THIN }}>Pérdida</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontSize: 11 }}>Ganancia</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontSize: 11, ...THIN }}>Cargo</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontSize: 11 }}>Abono</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={i} hover sx={nivelSx(r)}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, pl: r.nivel === 4 ? 2 : r.nivel === 3 ? 2 : 1 }}>
                      {r.nivel === 4 ? '' : r.codigo}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, pl: r.nivel === 4 ? 4 : undefined }}>
                      {r.nivel === 4 ? `↳ ${r.descripcion}` : r.descripcion}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 11 }}>{num(r.sd_ini)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 11 }}>{num(r.sa_ini)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 11, ...THIN_BODY }}>{num(r.debe_mov)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 11 }}>{num(r.haber_mov)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 11, ...THIN_BODY }}>{num(r.sd_fin)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 11 }}>{num(r.sa_fin)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 11, ...THIN_BODY }}>{num(r.activo)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 11 }}>{num(r.pasivo)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 11, ...THIN_BODY }}>{num(r.perdida)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 11 }}>{num(r.ganancia)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 11, ...THIN_BODY }}>{num(r.cargo)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 11 }}>{num(r.abono)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {hojas.length} cuenta(s) de detalle — Debe Mov.: {moneda === 'USD' ? '$' : 'S/'} {fmtNum(totalDebeMov)} — Haber Mov.: {moneda === 'USD' ? '$' : 'S/'} {fmtNum(totalHaberMov)}
            {Math.abs(totalDebeMov - totalHaberMov) > 0.001 && (
              <Chip size="small" color="error" label={`Descuadre: ${moneda === 'USD' ? '$' : 'S/'} ${fmtNum(totalDebeMov - totalHaberMov)} — use "Validar Cuadre"`} sx={{ ml: 1 }} />
            )}
          </Typography>
        </>
      )}

      {!loading && rows.length === 0 && desde && hasta && (
        <Alert severity="info">No hay movimientos en el rango de períodos seleccionado.</Alert>
      )}

      {/* ── Dialog Validador de Cuadre ── */}
      <Dialog open={cuadreOpen} onClose={() => setCuadreOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Validador de Cuadre</DialogTitle>
        <DialogContent>
          {cuadreLoading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>}
          {cuadreErr && <Alert severity="error">{cuadreErr}</Alert>}
          {cuadreData && cuadreData.count === 0 && (
            <Alert severity="success" icon={<CheckCircleIcon />}>
              No se encontraron asientos descuadrados{desde && hasta ? ` en el rango ${fmtPeriodo(desde)} a ${fmtPeriodo(hasta)}` : ' (todos los períodos)'}.
            </Alert>
          )}
          {cuadreData && cuadreData.count > 0 && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Se encontraron <b>{cuadreData.count}</b> asiento(s) con Debe ≠ Haber. Diferencia acumulada: <b>S/ {fmtNum(cuadreData.diferencia_total)}</b>
              </Alert>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Período</TableCell>
                      <TableCell>Documento</TableCell>
                      <TableCell>Fecha</TableCell>
                      <TableCell>Glosa</TableCell>
                      <TableCell align="right">Debe</TableCell>
                      <TableCell align="right">Haber</TableCell>
                      <TableCell align="right">Diferencia</TableCell>
                      {onIrAAsiento && <TableCell align="center">Acción</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cuadreData.rows.map(r => (
                      <TableRow key={r.id} hover>
                        <TableCell>{fmtPeriodo(r.periodo)}</TableCell>
                        <TableCell>{r.tipo_documento}-{r.numero}</TableCell>
                        <TableCell>{fmtFecha(r.fecha)}</TableCell>
                        <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.glosa}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{fmtNum(r.total_debe)}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{fmtNum(r.total_haber)}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'error.main', fontWeight: 'bold' }}>{fmtNum(r.diferencia)}</TableCell>
                        {onIrAAsiento && (
                          <TableCell align="center">
                            <Button size="small" variant="outlined" onClick={() => { setCuadreOpen(false); onIrAAsiento(r.id); }}>
                              Corregir
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {!onIrAAsiento && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Búsquelos por período + tipo de documento + número en "Asientos Contables" para corregirlos.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCuadreOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Libro Diario ────────────────────────────────────────────────────────────

function LibroDiario({ empresaId, empresa, periodos }) {
  const [periodo, setPeriodo] = useState('');
  const [moneda, setMoneda] = useState('PEN');
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [centroCostoId, setCentroCostoId] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingExcel, setLoadingExcel] = useState(false);
  const [loadingExcelDet, setLoadingExcelDet] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const LIMIT = 200;

  useEffect(() => {
    if (!empresaId) { setCentrosCosto([]); return; }
    axios.get('/api/contabilidad/centros-costo', { params: { empresa_id: empresaId } })
      .then(r => setCentrosCosto(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCentrosCosto([]));
  }, [empresaId]);

  // Reutiliza el reporte PLE (formato SUNAT, campos fijos) para que el Excel incluya TODO
  // el período -- este archivo NO se toca (es el regulatorio), el detalle gerencial con RUC/
  // Centro de Costo/N° Documento va en descargarExcelDetallado().
  const descargarExcel = async () => {
    if (!periodo) return;
    setLoadingExcel(true);
    try {
      const anio = periodo.substring(0, 4), mes = periodo.substring(4, 6);
      const r = await axios.get('/api/ple/excel/diario', {
        params: { empresa_id: empresaId, anio, mes }, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `LibroDiario_${periodo}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert('Error al descargar el Excel'); }
    setLoadingExcel(false);
  };

  // Excel gerencial (no regulatorio): todas las líneas del período con RUC/Razón Social del
  // auxiliar, N° Documento, Centro de Costo y Moneda/T.Cambio -- lo que pidió el contador
  // para conciliar, a diferencia del Excel PLE de arriba que respeta el formato SUNAT fijo.
  const descargarExcelDetallado = async () => {
    if (!periodo) return;
    setLoadingExcelDet(true);
    try {
      const r = await axios.get('/api/contabilidad/reportes/libro-diario', {
        params: { empresa_id: empresaId, periodo, page: 1, limit: 100000, moneda, centro_costo_id: centroCostoId || undefined }
      });
      const todas = r.data.data || [];
      const columns = [
        { header: 'Fecha', key: '_fecha', width: 12 },
        { header: 'Asiento', key: '_asiento', width: 14 },
        { header: 'Cuenta', key: '_cuenta', width: 12 },
        { header: 'Descripción cuenta', key: '_cuentaDesc', width: 30 },
        { header: 'Glosa', key: '_glosa', width: 36 },
        { header: 'RUC', key: '_ruc', width: 13 },
        { header: 'Razón Social', key: '_razon', width: 26 },
        { header: 'N° Documento', key: '_doc', width: 16 },
        { header: 'Centro de Costo', key: '_cc', width: 24 },
        { header: 'Moneda', key: '_moneda', width: 10 },
        { header: 'T. Cambio', key: '_tc', width: 10, type: 'number' },
        { header: 'Debe', key: '_debe', width: 14, type: 'number', totKey: 'debe' },
        { header: 'Haber', key: '_haber', width: 14, type: 'number', totKey: 'haber' },
      ];
      const rowsXlsx = todas.map(l => ({
        _fecha: fmtFecha(l.fecha), _asiento: `${l.tipo_documento}-${l.numero}`,
        _cuenta: l.cuenta, _cuentaDesc: l.cuenta_desc,
        _glosa: l.glosa_det || l.glosa,
        _ruc: l.auxiliar_ruc || '', _razon: l.auxiliar_nombre || '',
        _doc: l.doc_referencia || '',
        _cc: l.cc_codigo ? `${l.cc_codigo} - ${l.cc_nombre}` : '',
        _moneda: l.moneda_asiento || 'PEN', _tc: l.moneda_asiento === 'USD' ? parseFloat(l.tipo_cambio || 1) : '',
        _debe: l.debe, _haber: l.haber,
      }));
      const totals = { debe: todas.reduce((s, l) => s + parseFloat(l.debe || 0), 0), haber: todas.reduce((s, l) => s + parseFloat(l.haber || 0), 0) };
      await exportStyledXlsx({
        empresa, titulo: 'LIBRO DIARIO — DETALLE GERENCIAL', periodoLabel: fmtPeriodo(periodo),
        columns, rows: rowsXlsx, totals, filename: `LibroDiario_Detallado_${periodo}.xlsx`,
        sheetName: 'Libro Diario', landscape: true,
      });
    } catch (e) { alert('Error al descargar el Excel detallado'); }
    setLoadingExcelDet(false);
  };

  const buscar = useCallback(async (p = 1) => {
    if (!periodo) { setError('Seleccione un período'); return; }
    setLoading(true); setError('');
    try {
      const r = await axios.get('/api/contabilidad/reportes/libro-diario', {
        params: { empresa_id: empresaId, periodo, page: p, limit: LIMIT, moneda, centro_costo_id: centroCostoId || undefined }
      });
      setRows(r.data.data || []);
      setTotal(r.data.total || 0);
      setPage(p);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
    setLoading(false);
  }, [empresaId, periodo, moneda, centroCostoId]);

  // Agrupar filas por asiento (tipo_documento + numero)
  const grupos = [];
  let asientoActual = null;
  rows.forEach((row, i) => {
    const key = `${row.tipo_documento}-${row.numero}`;
    if (key !== asientoActual) {
      asientoActual = key;
      grupos.push({ key, fecha: row.fecha, tipo: row.tipo_documento, numero: row.numero, glosa: row.glosa, lineas: [] });
    }
    grupos[grupos.length - 1].lineas.push(row);
  });

  const totalDebe = rows.reduce((s, r) => s + parseFloat(r.debe || 0), 0);
  const totalHaber = rows.reduce((s, r) => s + parseFloat(r.haber || 0), 0);

  return (
    <Box>
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField select label="Período" size="small" value={periodo}
            onChange={e => { setPeriodo(e.target.value); setRows([]); }} sx={{ minWidth: 200 }}>
            {periodos.map(p => (
              <MenuItem key={p.periodo} value={p.periodo}>{fmtPeriodo(p.periodo)}</MenuItem>
            ))}
          </TextField>
          <TextField select label="Moneda" size="small" value={moneda}
            onChange={e => setMoneda(e.target.value)} sx={{ minWidth: 130 }}>
            <MenuItem value="PEN">S/ Soles</MenuItem>
            <MenuItem value="USD">$ Dólares</MenuItem>
          </TextField>
          <TextField select label="Centro de Costo" size="small" value={centroCostoId}
            onChange={e => setCentroCostoId(e.target.value)} sx={{ minWidth: 200 }}
            SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
            <MenuItem value="">Todos</MenuItem>
            {centrosCosto.map(cc => (
              <MenuItem key={cc.id} value={String(cc.id)}>{cc.codigo} — {cc.descripcion}</MenuItem>
            ))}
          </TextField>
          <Button variant="contained"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            onClick={() => buscar(1)} disabled={loading}>
            Generar
          </Button>
          {rows.length > 0 && (<>
            <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}>
              Imprimir
            </Button>
            <Button variant="outlined" color="success" disabled={loadingExcel}
              startIcon={loadingExcel ? <CircularProgress size={16} /> : <DownloadIcon />} onClick={descargarExcel}>
              Excel PLE
            </Button>
            <Button variant="outlined" color="success" disabled={loadingExcelDet}
              startIcon={loadingExcelDet ? <CircularProgress size={16} /> : <DownloadIcon />} onClick={descargarExcelDetallado}>
              Excel detallado
            </Button>
          </>)}
          {total > 0 && (
            <Typography variant="body2" color="text.secondary">
              {total} líneas — mostrando {rows.length}
            </Typography>
          )}
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {grupos.length > 0 && (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" align="center">{empresa?.razon_social}</Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              Libro Diario — {fmtPeriodo(periodo)}
              {' — '}<Box component="span" sx={{ fontWeight: 'bold', color: moneda === 'USD' ? 'info.main' : 'text.secondary' }}>
                Expresado en {moneda === 'USD' ? 'Dólares (US$)' : 'Soles (S/)'}
              </Box>
            </Typography>
          </Box>
          <TableContainer component={Paper} elevation={2} sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 90 }}>Fecha</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 80 }}>Asiento</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 80 }}>Cuenta</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Descripción / Glosa</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 150 }}>Auxiliar</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 110 }}>N° Documento</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 130 }}>C. Costo</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 110 }} align="right">Debe</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 110 }} align="right">Haber</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {grupos.map(g => (
                  <React.Fragment key={g.key}>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: 12 }}>{fmtFecha(g.fecha)}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: 12 }}>
                        {g.tipo}-{g.numero}
                      </TableCell>
                      <TableCell />
                      <TableCell sx={{ fontStyle: 'italic', color: 'text.secondary', fontSize: 12 }} colSpan={6}>
                        {g.glosa}
                      </TableCell>
                    </TableRow>
                    {g.lineas.map((l, i) => (
                      <TableRow key={i} hover>
                        <TableCell />
                        <TableCell />
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, color: '#388e3c', fontWeight: 'bold' }}>
                          {l.cuenta}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          {l.cuenta_desc}
                          {l.glosa_det && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              — {l.glosa_det}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: 11 }}>
                          {l.auxiliar_ruc && <Typography variant="caption" display="block" fontFamily="monospace" color="text.secondary">{l.auxiliar_ruc}</Typography>}
                          {l.auxiliar_nombre || ''}
                        </TableCell>
                        <TableCell sx={{ fontSize: 11, fontFamily: 'monospace' }}>{l.doc_referencia || ''}</TableCell>
                        <TableCell sx={{ fontSize: 11 }}>{l.cc_codigo ? `${l.cc_codigo} - ${l.cc_nombre}` : ''}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {parseFloat(l.debe) > 0 ? fmtNum(l.debe) : ''}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {parseFloat(l.haber) > 0 ? fmtNum(l.haber) : ''}
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell colSpan={7} sx={{ fontWeight: 'bold', textAlign: 'right' }}>TOTALES DEL PERÍODO</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>{fmtNumSaldo(totalDebe)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>{fmtNumSaldo(totalHaber)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          {total > LIMIT && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Mostrando {rows.length} de {total} líneas. Use filtros de fecha para reducir el resultado.
            </Alert>
          )}
        </>
      )}

      {!loading && rows.length === 0 && periodo && (
        <Alert severity="info">No hay movimientos activos en el período seleccionado.</Alert>
      )}
    </Box>
  );
}

// ─── Libro Mayor ─────────────────────────────────────────────────────────────

function LibroMayor({ empresaId, empresa, periodos }) {
  const [periodoDesde, setPeriodoDesde] = useState('');
  const [periodoHasta, setPeriodoHasta] = useState('');
  const [moneda, setMoneda] = useState('PEN');
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [centroCostoId, setCentroCostoId] = useState('');
  const [cuentaOpts, setCuentaOpts] = useState([]);
  const [cuentaSel, setCuentaSel] = useState(null);
  const [inputCuenta, setInputCuenta] = useState('');
  const [buscandoCuenta, setBuscandoCuenta] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!empresaId) { setCentrosCosto([]); return; }
    axios.get('/api/contabilidad/centros-costo', { params: { empresa_id: empresaId } })
      .then(r => setCentrosCosto(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCentrosCosto([]));
  }, [empresaId]);

  const buscarCuentas = useCallback(async (v) => {
    if (!v || v.length < 2 || !empresaId) { setCuentaOpts([]); return; }
    setBuscandoCuenta(true);
    try {
      const r = await axios.get('/api/contabilidad/plan', {
        params: { empresa_id: empresaId, buscar: v, limit: 40 }
      });
      setCuentaOpts(Array.isArray(r.data) ? r.data : []);
    } catch {}
    setBuscandoCuenta(false);
  }, [empresaId]);

  const buscar = async () => {
    if (!cuentaSel) { setError('Seleccione una cuenta contable'); return; }
    setLoading(true); setError('');
    try {
      const params = { empresa_id: empresaId, cuenta_id: cuentaSel.id, moneda };
      if (periodoDesde) params.periodo_desde = periodoDesde;
      if (periodoHasta) params.periodo_hasta = periodoHasta;
      if (centroCostoId) params.centro_costo_id = centroCostoId;
      const r = await axios.get('/api/contabilidad/reportes/libro-mayor', { params });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
    setLoading(false);
  };

  const totalDebe = (data?.data || []).reduce((s, r) => s + parseFloat(r.debe || 0), 0);
  const totalHaber = (data?.data || []).reduce((s, r) => s + parseFloat(r.haber || 0), 0);
  const saldoFinal = data?.data?.length > 0 ? parseFloat(data.data[data.data.length - 1].saldo || 0) : 0;

  return (
    <Box>
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Autocomplete
            size="small"
            options={cuentaOpts}
            loading={buscandoCuenta}
            filterOptions={x => x}
            getOptionLabel={o => o ? `${o.codigo} — ${o.descripcion}` : ''}
            value={cuentaSel}
            inputValue={inputCuenta}
            onInputChange={(_, v, reason) => {
              setInputCuenta(v);
              if (reason === 'input') buscarCuentas(v);
              if (reason === 'clear') { setCuentaSel(null); setCuentaOpts([]); }
            }}
            onChange={(_, v) => { setCuentaSel(v); if (v) setInputCuenta(`${v.codigo} — ${v.descripcion}`); }}
            renderInput={p => (
              <TextField {...p} label="Cuenta contable" placeholder="Buscar por código o nombre..."
                InputProps={{
                  ...p.InputProps,
                  endAdornment: (
                    <>{buscandoCuenta && <CircularProgress size={14} />}{p.InputProps.endAdornment}</>
                  )
                }} />
            )}
            renderOption={(props, o) => (
              <li {...props} key={o.id}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline' }}>
                  <Typography sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#388e3c', minWidth: 70, fontSize: 12 }}>
                    {o.codigo}
                  </Typography>
                  <Typography variant="body2">{o.descripcion}</Typography>
                </Box>
              </li>
            )}
            isOptionEqualToValue={(o, v) => o.id === v?.id}
            noOptionsText={inputCuenta.length < 2 ? 'Escriba al menos 2 caracteres' : 'Sin resultados'}
            sx={{ minWidth: 300 }}
          />
          <TextField select label="Período desde" size="small" value={periodoDesde}
            onChange={e => setPeriodoDesde(e.target.value)} sx={{ minWidth: 170 }}>
            <MenuItem value="">(Todos)</MenuItem>
            {periodos.map(p => <MenuItem key={p.periodo} value={p.periodo}>{fmtPeriodo(p.periodo)}</MenuItem>)}
          </TextField>
          <TextField select label="Período hasta" size="small" value={periodoHasta}
            onChange={e => setPeriodoHasta(e.target.value)} sx={{ minWidth: 170 }}>
            <MenuItem value="">(Todos)</MenuItem>
            {periodos.map(p => <MenuItem key={p.periodo} value={p.periodo}>{fmtPeriodo(p.periodo)}</MenuItem>)}
          </TextField>
          <TextField select label="Moneda" size="small" value={moneda}
            onChange={e => setMoneda(e.target.value)} sx={{ minWidth: 130 }}>
            <MenuItem value="PEN">S/ Soles</MenuItem>
            <MenuItem value="USD">$ Dólares</MenuItem>
          </TextField>
          <TextField select label="Centro de Costo" size="small" value={centroCostoId}
            onChange={e => setCentroCostoId(e.target.value)} sx={{ minWidth: 200 }}
            SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
            <MenuItem value="">Todos</MenuItem>
            {centrosCosto.map(cc => (
              <MenuItem key={cc.id} value={String(cc.id)}>{cc.codigo} — {cc.descripcion}</MenuItem>
            ))}
          </TextField>
          <Button variant="contained"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            onClick={buscar} disabled={loading}>
            Generar
          </Button>
          {data && (<>
            <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}>
              Imprimir
            </Button>
            <Button variant="outlined" color="success" startIcon={<DownloadIcon />} onClick={() => {
              const columns = [
                { header: 'Período', key: '_periodo', width: 10 },
                { header: 'Fecha', key: '_fecha', width: 12 },
                { header: 'Asiento', key: '_asiento', width: 12 },
                { header: 'Glosa', key: '_glosa', width: 36 },
                { header: 'RUC', key: '_ruc', width: 13 },
                { header: 'Razón Social', key: '_razon', width: 24 },
                { header: 'N° Documento', key: '_doc', width: 16 },
                { header: 'Centro de Costo', key: '_cc', width: 22 },
                { header: 'Debe', key: '_debe', width: 15, type: 'number', totKey: 'debe' },
                { header: 'Haber', key: '_haber', width: 15, type: 'number', totKey: 'haber' },
                { header: 'Saldo', key: '_saldo', width: 16, type: 'number' },
              ];
              const filas = (data.data || []).map(r => ({
                _periodo: r.periodo, _fecha: fmtFecha(r.fecha), _asiento: `${r.tipo_documento}-${r.numero}`,
                _glosa: r.glosa_det ? `${r.glosa} — ${r.glosa_det}` : r.glosa,
                _ruc: r.auxiliar_ruc || '', _razon: r.auxiliar_nombre || '',
                _doc: r.doc_referencia || '',
                _cc: r.cc_codigo ? `${r.cc_codigo} - ${r.cc_nombre}` : '',
                _debe: +parseFloat(r.debe || 0).toFixed(2), _haber: +parseFloat(r.haber || 0).toFixed(2),
                _saldo: +parseFloat(r.saldo || 0).toFixed(2),
              }));
              exportStyledXlsx({
                empresa, titulo: `LIBRO MAYOR — ${data.cuenta?.codigo} ${data.cuenta?.descripcion}`,
                periodoLabel: `${periodoDesde ? fmtPeriodo(periodoDesde) : 'Inicio'} a ${periodoHasta ? fmtPeriodo(periodoHasta) : 'Fin'}`,
                columns, rows: filas, totals: { debe: totalDebe, haber: totalHaber },
                filename: `LibroMayor_${data.cuenta?.codigo}_${periodoDesde || 'ini'}_${periodoHasta || 'fin'}.xlsx`,
                sheetName: 'Libro Mayor', landscape: true,
              });
            }}>Excel</Button>
          </>)}
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" align="center">{empresa?.razon_social}</Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              Libro Mayor — Cuenta {data.cuenta?.codigo}: {data.cuenta?.descripcion}
            </Typography>
            <Typography variant="body2" align="center" sx={{ fontWeight: 'bold', color: moneda === 'USD' ? 'info.main' : 'text.secondary' }}>
              Expresado en {moneda === 'USD' ? 'Dólares (US$)' : 'Soles (S/)'}
            </Typography>
            {(periodoDesde || periodoHasta) && (
              <Typography variant="body2" color="text.secondary" align="center">
                {periodoDesde ? fmtPeriodo(periodoDesde) : '—'} a {periodoHasta ? fmtPeriodo(periodoHasta) : '—'}
              </Typography>
            )}
          </Box>

          <TableContainer component={Paper} elevation={2} sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  {['Período', 'Fecha', 'Asiento', 'Glosa', 'Auxiliar', 'N° Documento', 'C. Costo', 'Debe', 'Haber', 'Saldo'].map(h => (
                    <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold',
                      ...((['Debe','Haber','Saldo'].includes(h)) ? { minWidth: 110 } : {}) }}
                      align={['Debe','Haber','Saldo'].includes(h) ? 'right' : 'left'}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Sin movimientos en esta cuenta para el rango seleccionado
                    </TableCell>
                  </TableRow>
                ) : data.data.map((r, i) => {
                  const saldo = parseFloat(r.saldo || 0);
                  return (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontSize: 12 }}>{r.periodo}</TableCell>
                      <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmtFecha(r.fecha)}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 'bold' }}>
                        {r.tipo_documento}-{r.numero}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {r.glosa}
                        {r.glosa_det && (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>— {r.glosa_det}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11 }}>
                        {r.auxiliar_ruc && <Typography variant="caption" display="block" fontFamily="monospace" color="text.secondary">{r.auxiliar_ruc}</Typography>}
                        {r.auxiliar_nombre || ''}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, fontFamily: 'monospace' }}>{r.doc_referencia || ''}</TableCell>
                      <TableCell sx={{ fontSize: 11 }}>{r.cc_codigo ? `${r.cc_codigo} - ${r.cc_nombre}` : ''}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {parseFloat(r.debe) > 0 ? fmtNum(r.debe) : ''}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {parseFloat(r.haber) > 0 ? fmtNum(r.haber) : ''}
                      </TableCell>
                      <TableCell align="right" sx={{
                        fontFamily: 'monospace', fontSize: 12, fontWeight: 'bold',
                        color: saldo > 0 ? '#1565c0' : saldo < 0 ? '#c62828' : 'text.secondary'
                      }}>
                        {fmtNumSaldo(Math.abs(saldo))}
                        <Typography variant="caption" sx={{ ml: 0.5, fontWeight: 'normal' }}>
                          {saldo > 0 ? 'D' : saldo < 0 ? 'A' : ''}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell colSpan={4} align="right" sx={{ fontWeight: 'bold' }}>TOTALES</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>{fmtNumSaldo(totalDebe)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>{fmtNumSaldo(totalHaber)}</TableCell>
                  <TableCell align="right" sx={{
                    fontWeight: 'bold', fontFamily: 'monospace',
                    color: saldoFinal > 0 ? '#1565c0' : saldoFinal < 0 ? '#c62828' : 'inherit'
                  }}>
                    {fmtNumSaldo(Math.abs(saldoFinal))}
                    <Typography variant="caption" sx={{ ml: 0.5 }}>
                      {saldoFinal > 0 ? 'D' : saldoFinal < 0 ? 'A' : ''}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {data.data.length} movimiento(s) — Saldo normal: {data.cuenta?.saldo_normal === 'D' ? 'Deudor' : 'Acreedor'}
          </Typography>
        </>
      )}
    </Box>
  );
}

// ─── Estado de Resultados ────────────────────────────────────────────────────

const TIPO_COLOR = { INGRESO: '#1b5e20', GASTO: '#b71c1c', COSTO: '#c62828' };

// Formato "hoja de trabajo" único para los 3 formatos del Estado de Resultados + "Por
// inventario" (Balance): clase (2 díg.) en negrita con subtotal, subgrupo (3 díg.) en
// negrita con subtotal, cuenta hoja al detalle -- columnas Debe/Haber (SUMAS del período)
// + 2 columnas de clasificación cuya etiqueta cambia según el formato (Cargo/Abono en
// Naturaleza, Pérdida/Ganancia en Función, Activo/Pasivo en Inventario) pero salen de la
// MISMA lógica que el Balance de Comprobación 3.17 (ver agruparJerarquico en el backend).
function SectionHojaTrabajo({ titulo, grupos, col1Label, col1Key, col2Label, col2Key, colorFondo }) {
  const total1 = (grupos || []).reduce((s, g) => s + (g[col1Key] || 0), 0);
  const total2 = (grupos || []).reduce((s, g) => s + (g[col2Key] || 0), 0);
  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ bgcolor: colorFondo, px: 2, py: 0.8, borderRadius: '4px 4px 0 0' }}>
        <Typography variant="subtitle2" fontWeight="bold" color="white">{titulo}</Typography>
      </Box>
      <TableContainer component={Paper} variant="outlined" sx={{ borderTop: 0, borderRadius: '0 0 4px 4px', overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', fontSize: 11, width: 90 }}>Cuenta</TableCell>
              <TableCell sx={{ fontWeight: 'bold', fontSize: 11 }}>Descripción</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: 11, width: 100 }}>Debe</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: 11, width: 100 }}>Haber</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: 11, width: 100 }}>{col1Label}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: 11, width: 100 }}>{col2Label}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(grupos || []).map(g => (
              <React.Fragment key={g.codigo}>
                {g.subgrupos.map(sg => (
                  <React.Fragment key={sg.codigo}>
                    {sg.cuentas.map(c => (
                      <TableRow key={c.codigo} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, color: '#777', pl: 4 }}>{c.codigo}</TableCell>
                        <TableCell sx={{ fontSize: 11, color: '#555' }}>{c.descripcion}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 11 }}>{c.debe ? fmtNumSaldo(c.debe) : ''}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 11 }}>{c.haber ? fmtNumSaldo(c.haber) : ''}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 11 }}>{c[col1Key] ? fmtNumSaldo(c[col1Key]) : ''}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 11 }}>{c[col2Key] ? fmtNumSaldo(c[col2Key]) : ''}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: '#fafafa' }}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 'bold', color: '#1565c0', pl: 2 }}>{sg.codigo}</TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 'bold' }}>{sg.descripcion}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 'bold' }}>{fmtNumSaldo(sg.debe)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 'bold' }}>{fmtNumSaldo(sg.haber)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 'bold' }}>{fmtNumSaldo(sg[col1Key])}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 'bold' }}>{fmtNumSaldo(sg[col2Key])}</TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
                <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 'bold' }}>{g.codigo}</TableCell>
                  <TableCell sx={{ fontSize: 13, fontWeight: 'bold' }}>{g.descripcion}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 'bold' }}>{fmtNumSaldo(g.debe)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 'bold' }}>{fmtNumSaldo(g.haber)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 'bold' }}>{fmtNumSaldo(g[col1Key])}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 'bold' }}>{fmtNumSaldo(g[col2Key])}</TableCell>
                </TableRow>
              </React.Fragment>
            ))}
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell colSpan={4} sx={{ fontWeight: 'bold', textAlign: 'right', pr: 2, fontSize: 13 }}>
                TOTAL {titulo}
              </TableCell>
              <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: 14, pr: 1 }}>
                {total1 ? fmtNumSaldo(total1) : ''}
              </TableCell>
              <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: 14, pr: 3 }}>
                {total2 ? fmtNumSaldo(total2) : ''}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// Qué par de columnas de clasificación (ver agruparJerarquico en el backend) le corresponde
// a cada formato -- las 3 salen del mismo cálculo (cargo=pérdida, abono=ganancia siempre),
// solo cambia la etiqueta y el alcance de cuentas incluidas.
const COLS_FORMATO = {
  funcion: { label1: 'Pérdida', key1: 'perdida', label2: 'Ganancia', key2: 'ganancia' },
  naturaleza: { label1: 'Cargo', key1: 'cargo', label2: 'Abono', key2: 'abono' },
  inventario: { label1: 'Activo', key1: 'activo', label2: 'Pasivo', key2: 'pasivo' },
};

function EstadoResultados({ empresaId, empresa, periodos }) {
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [moneda, setMoneda] = useState('PEN');
  const [formato, setFormato] = useState('funcion');
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [centroCostoId, setCentroCostoId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!empresaId) { setCentrosCosto([]); return; }
    axios.get('/api/contabilidad/centros-costo', { params: { empresa_id: empresaId } })
      .then(r => setCentrosCosto(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCentrosCosto([]));
  }, [empresaId]);

  const buscar = async () => {
    if (!desde || !hasta) { setError('Seleccione período desde y hasta'); return; }
    setLoading(true); setError(''); setData(null);
    try {
      const r = await axios.get('/api/contabilidad/reportes/estado-resultados', {
        params: { empresa_id: empresaId, periodo_desde: desde, periodo_hasta: hasta, moneda, formato, centro_costo_id: centroCostoId || undefined }
      });
      setData(r.data);
    } catch (e) { setError(e.response?.data?.error || e.message); }
    setLoading(false);
  };

  const esUtilidad = data && data.resultado_neto >= 0;

  return (
    <Box>
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField select label="Período desde" size="small" value={desde}
            onChange={e => setDesde(e.target.value)} sx={{ minWidth: 180 }}>
            {periodos.map(p => <MenuItem key={p.periodo} value={p.periodo}>{fmtPeriodo(p.periodo)}</MenuItem>)}
          </TextField>
          <TextField select label="Período hasta" size="small" value={hasta}
            onChange={e => setHasta(e.target.value)} sx={{ minWidth: 180 }}>
            {periodos.map(p => <MenuItem key={p.periodo} value={p.periodo}>{fmtPeriodo(p.periodo)}</MenuItem>)}
          </TextField>
          <TextField select label="Moneda" size="small" value={moneda}
            onChange={e => setMoneda(e.target.value)} sx={{ minWidth: 130 }}>
            <MenuItem value="PEN">S/ Soles</MenuItem>
            <MenuItem value="USD">$ Dólares</MenuItem>
          </TextField>
          <TextField select label="Formato" size="small" value={formato}
            onChange={e => setFormato(e.target.value)} sx={{ minWidth: 170 }}>
            <MenuItem value="funcion">Por función</MenuItem>
            <MenuItem value="naturaleza">Por naturaleza</MenuItem>
            <MenuItem value="inventario">Por inventario</MenuItem>
          </TextField>
          <TextField select label="Centro de Costo" size="small" value={centroCostoId}
            onChange={e => setCentroCostoId(e.target.value)} sx={{ minWidth: 200 }}
            SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
            <MenuItem value="">Todos</MenuItem>
            {centrosCosto.map(cc => (
              <MenuItem key={cc.id} value={String(cc.id)}>{cc.codigo} — {cc.descripcion}</MenuItem>
            ))}
          </TextField>
          <Button variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            onClick={buscar} disabled={loading}>
            Generar
          </Button>
          {data && (<>
            <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}>Imprimir</Button>
            <Button variant="outlined" color="success" startIcon={<DownloadIcon />} onClick={() => {
              const cols = COLS_FORMATO[data.formato] || COLS_FORMATO.funcion;
              const columns = [
                { header: 'Código', key: '_codigo', width: 12 },
                { header: 'Concepto', key: '_nombre', width: 42 },
                { header: 'Debe', key: '_debe', width: 14, type: 'number' },
                { header: 'Haber', key: '_haber', width: 14, type: 'number' },
                { header: cols.label1, key: '_col1', width: 14, type: 'number' },
                { header: cols.label2, key: '_col2', width: 14, type: 'number' },
              ];
              const filas = [];
              const volcar = (grupos) => (grupos || []).forEach(g => {
                filas.push({ _codigo: g.codigo, _nombre: g.descripcion, _debe: +g.debe.toFixed(2), _haber: +g.haber.toFixed(2), _col1: +g[cols.key1].toFixed(2), _col2: +g[cols.key2].toFixed(2) });
                g.subgrupos.forEach(sg => {
                  filas.push({ _codigo: sg.codigo, _nombre: sg.descripcion, _debe: +sg.debe.toFixed(2), _haber: +sg.haber.toFixed(2), _col1: +sg[cols.key1].toFixed(2), _col2: +sg[cols.key2].toFixed(2) });
                  sg.cuentas.forEach(c => filas.push({ _codigo: c.codigo, _nombre: c.descripcion, _debe: +c.debe.toFixed(2), _haber: +c.haber.toFixed(2), _col1: +(c[cols.key1] || 0).toFixed(2), _col2: +(c[cols.key2] || 0).toFixed(2) }));
                });
              });
              if (data.formato === 'inventario') {
                volcar(data.grupos || []);
                filas.push({ _codigo: '', _nombre: 'TOTAL ACTIVO', _debe: 0, _haber: 0, _col1: +parseFloat(data.total_activo || 0).toFixed(2), _col2: 0 });
                filas.push({ _codigo: '', _nombre: 'TOTAL PASIVO', _debe: 0, _haber: 0, _col1: 0, _col2: +parseFloat(data.total_pasivo || 0).toFixed(2) });
              } else {
                volcar(data.ingresos || []);
                filas.push({ _codigo: '', _nombre: 'TOTAL INGRESOS', _debe: 0, _haber: 0, _col1: 0, _col2: +parseFloat(data.total_ingresos || 0).toFixed(2) });
                volcar(data.gastos || []);
                filas.push({ _codigo: '', _nombre: 'TOTAL GASTOS Y COSTOS', _debe: 0, _haber: 0, _col1: +parseFloat(data.total_gastos || 0).toFixed(2), _col2: 0 });
                filas.push({ _codigo: '', _nombre: data.resultado_neto >= 0 ? 'UTILIDAD NETA' : 'PÉRDIDA NETA', _debe: 0, _haber: 0, _col1: 0, _col2: +parseFloat(data.resultado_neto || 0).toFixed(2) });
              }
              const tituloFmt = { funcion: 'POR FUNCIÓN', naturaleza: 'POR NATURALEZA', inventario: 'POR INVENTARIO (BALANCE GENERAL)' }[data.formato];
              exportStyledXlsx({
                empresa, titulo: `${data.formato === 'inventario' ? 'BALANCE GENERAL' : 'ESTADO DE RESULTADOS'} — ${tituloFmt}`, periodoLabel: `${fmtPeriodo(desde)} a ${fmtPeriodo(hasta)}`,
                columns, rows: filas, filename: `EstadoResultados_${data.formato}_${desde}_${hasta}.xlsx`, sheetName: 'Estado de Resultados',
              });
            }}>Excel</Button>
            <Button variant="outlined" color="error" startIcon={<PictureAsPdfIcon />} onClick={() => {
              const cols = COLS_FORMATO[data.formato] || COLS_FORMATO.funcion;
              const rows = [];
              const volcarPdf = (grupos) => (grupos || []).forEach(g => {
                rows.push([g.codigo, g.descripcion, fmtNum(g[cols.key1]), fmtNum(g[cols.key2])]);
                g.subgrupos.forEach(sg => {
                  rows.push([sg.codigo, '  ' + sg.descripcion, fmtNum(sg[cols.key1]), fmtNum(sg[cols.key2])]);
                  sg.cuentas.forEach(c => rows.push([c.codigo, '    ' + c.descripcion, fmtNum(c[cols.key1] || 0), fmtNum(c[cols.key2] || 0)]));
                });
              });
              if (data.formato === 'inventario') {
                volcarPdf(data.grupos || []);
                rows.push(['', 'TOTAL ACTIVO', fmtNum(data.total_activo), '']);
                rows.push(['', 'TOTAL PASIVO', '', fmtNum(data.total_pasivo)]);
              } else {
                volcarPdf(data.ingresos || []);
                rows.push(['', 'TOTAL INGRESOS', '', fmtNum(data.total_ingresos)]);
                volcarPdf(data.gastos || []);
                rows.push(['', 'TOTAL GASTOS Y COSTOS', fmtNum(data.total_gastos), '']);
                rows.push(['', data.resultado_neto >= 0 ? 'UTILIDAD NETA' : 'PÉRDIDA NETA', '', fmtNum(Math.abs(data.resultado_neto))]);
              }
              exportPdf(`${data.formato === 'inventario' ? 'Balance General' : 'Estado de Resultados'} — ${empresa?.razon_social}`,
                `Períodos ${fmtPeriodo(desde)} a ${fmtPeriodo(hasta)}`,
                ['Código', 'Cuenta', cols.label1, cols.label2], rows, `EstadoResultados_${data.formato}_${desde}_${hasta}.pdf`);
            }}>PDF</Button>
          </>)}
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <Box sx={{ maxWidth: 1100 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h6" fontWeight="bold">{empresa?.razon_social}</Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {data.formato === 'inventario'
                ? 'Balance General — Por inventario'
                : `Estado de Resultados — ${{ funcion: 'Por función', naturaleza: 'Por naturaleza' }[data.formato]}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Períodos {fmtPeriodo(desde)} — {fmtPeriodo(hasta)}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: moneda === 'USD' ? 'info.main' : 'text.secondary' }}>
              Expresado en {moneda === 'USD' ? 'Dólares (US$)' : 'Soles (S/)'}
            </Typography>
          </Box>

          {data.formato === 'inventario' ? (
            <SectionHojaTrabajo titulo="BALANCE GENERAL" grupos={data.grupos}
              col1Label="Activo" col1Key="activo" col2Label="Pasivo" col2Key="pasivo" colorFondo="#0d47a1" />
          ) : (
            <>
              <SectionHojaTrabajo titulo="INGRESOS" grupos={data.ingresos}
                col1Label={COLS_FORMATO[data.formato].label1} col1Key={COLS_FORMATO[data.formato].key1}
                col2Label={COLS_FORMATO[data.formato].label2} col2Key={COLS_FORMATO[data.formato].key2}
                colorFondo="#1b5e20" />
              <Box sx={{ my: 1 }} />
              <SectionHojaTrabajo titulo="GASTOS Y COSTOS" grupos={data.gastos}
                col1Label={COLS_FORMATO[data.formato].label1} col1Key={COLS_FORMATO[data.formato].key1}
                col2Label={COLS_FORMATO[data.formato].label2} col2Key={COLS_FORMATO[data.formato].key2}
                colorFondo="#b71c1c" />
            </>
          )}

          {data.formato === 'inventario' ? (
            <Paper elevation={3} sx={{
              mt: 2, p: 2,
              bgcolor: data.cuadra ? '#e8f5e9' : '#ffebee',
              border: `2px solid ${data.cuadra ? '#2e7d32' : '#c62828'}`
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {data.cuadra ? <CheckCircleIcon sx={{ color: '#2e7d32' }} /> : <WarningIcon sx={{ color: '#c62828' }} />}
                  <Typography variant="h6" fontWeight="bold" color={data.cuadra ? '#2e7d32' : '#c62828'}>
                    {data.cuadra ? 'BALANCE CUADRA' : 'BALANCE NO CUADRA — REVISAR ASIENTOS'}
                  </Typography>
                </Box>
                <Typography variant="body2" fontFamily="monospace">
                  Activo {fmtNumSaldo(data.total_activo)} — Pasivo {fmtNumSaldo(data.total_pasivo)}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {(data.grupos || []).length} clase(s)
              </Typography>
            </Paper>
          ) : (
            <Paper elevation={3} sx={{
              mt: 2, p: 2,
              bgcolor: esUtilidad ? '#e8f5e9' : '#ffebee',
              border: `2px solid ${esUtilidad ? '#2e7d32' : '#c62828'}`
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon sx={{ color: esUtilidad ? '#2e7d32' : '#c62828', fontSize: 28 }} />
                  <Typography variant="h6" fontWeight="bold" color={esUtilidad ? '#2e7d32' : '#c62828'}>
                    {esUtilidad ? 'UTILIDAD DEL EJERCICIO' : 'PÉRDIDA DEL EJERCICIO'}
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight="bold" fontFamily="monospace"
                  color={esUtilidad ? '#2e7d32' : '#c62828'}>
                  {esUtilidad ? '' : '('}{fmtNumSaldo(Math.abs(data.resultado_neto))}{esUtilidad ? '' : ')'}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {(data.ingresos || []).length} clase(s) ingreso · {(data.gastos || []).length} clase(s) gasto/costo
              </Typography>
            </Paper>
          )}
        </Box>
      )}

      {!loading && !data && desde && hasta && (
        <Alert severity="info">No hay movimientos de resultados en el rango seleccionado.</Alert>
      )}
    </Box>
  );
}

// ─── Balance General ─────────────────────────────────────────────────────────

function GrupoBalance({ titulo, filas, total, color }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ bgcolor: color, px: 2, py: 0.8, borderRadius: '4px 4px 0 0' }}>
        <Typography variant="subtitle2" fontWeight="bold" color="white">{titulo}</Typography>
      </Box>
      <TableContainer component={Paper} variant="outlined" sx={{ borderTop: 0, borderRadius: '0 0 4px 4px', overflowX: 'auto' }}>
        <Table size="small">
          <TableBody>
            {filas.map((r, i) => (
              <TableRow key={i} hover>
                <TableCell sx={{ width: 90, fontFamily: 'monospace', fontSize: 12, color: '#388e3c', fontWeight: 'bold', pl: 3 }}>
                  {r.codigo}
                </TableCell>
                <TableCell sx={{ fontSize: 13 }}>{r.descripcion}</TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 13, pr: 3, whiteSpace: 'nowrap' }}>
                  {r.saldo < 0
                    ? <span style={{ color: '#c62828' }}>({fmtNumSaldo(Math.abs(r.saldo))})</span>
                    : fmtNumSaldo(r.saldo)
                  }
                </TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell colSpan={2} sx={{ fontWeight: 'bold', textAlign: 'right', pr: 2, fontSize: 13 }}>
                TOTAL {titulo}
              </TableCell>
              <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: 14, pr: 3 }}>
                {total < 0
                  ? <span style={{ color: '#c62828' }}>({fmtNumSaldo(Math.abs(total))})</span>
                  : fmtNumSaldo(total)
                }
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function BalanceGeneral({ empresaId, empresa, periodos }) {
  const [hasta, setHasta] = useState('');
  const [moneda, setMoneda] = useState('PEN');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const buscar = async () => {
    if (!hasta) { setError('Seleccione el período de corte'); return; }
    setLoading(true); setError(''); setData(null);
    try {
      const r = await axios.get('/api/contabilidad/reportes/balance-general', {
        params: { empresa_id: empresaId, periodo_hasta: hasta, moneda }
      });
      setData(r.data);
    } catch (e) { setError(e.response?.data?.error || e.message); }
    setLoading(false);
  };

  const esUtilidad = data && data.resultado_ejercicio >= 0;

  return (
    <Box>
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField select label="Período de corte" size="small" value={hasta}
            onChange={e => setHasta(e.target.value)} sx={{ minWidth: 200 }}
            helperText="Saldos acumulados hasta este período">
            {periodos.map(p => <MenuItem key={p.periodo} value={p.periodo}>{fmtPeriodo(p.periodo)}</MenuItem>)}
          </TextField>
          <TextField select label="Moneda" size="small" value={moneda}
            onChange={e => setMoneda(e.target.value)} sx={{ minWidth: 130 }}>
            <MenuItem value="PEN">S/ Soles</MenuItem>
            <MenuItem value="USD">$ Dólares</MenuItem>
          </TextField>
          <Button variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            onClick={buscar} disabled={loading}>
            Generar
          </Button>
          {data && (<>
            <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}>Imprimir</Button>
            <Button variant="outlined" color="success" startIcon={<DownloadIcon />} onClick={() => {
              const columns = [
                { header: 'Código', key: '_codigo', width: 12 },
                { header: 'Cuenta', key: '_nombre', width: 48 },
                { header: 'Activo', key: '_activo', width: 18, type: 'number' },
                { header: 'Pasivo + Patrimonio', key: '_pasivopatrimonio', width: 20, type: 'number' },
              ];
              const filas = [];
              (data.activos||[]).forEach(f => filas.push({ _codigo: f.codigo, _nombre: f.nombre, _activo: +parseFloat(f.saldo||0).toFixed(2), _pasivopatrimonio: '' }));
              filas.push({ _codigo: '', _nombre: 'TOTAL ACTIVO', _activo: +parseFloat(data.total_activo||0).toFixed(2), _pasivopatrimonio: '' });
              (data.pasivos||[]).forEach(f => filas.push({ _codigo: f.codigo, _nombre: f.nombre, _activo: '', _pasivopatrimonio: +parseFloat(f.saldo||0).toFixed(2) }));
              (data.patrimonio||[]).forEach(f => filas.push({ _codigo: f.codigo, _nombre: f.nombre, _activo: '', _pasivopatrimonio: +parseFloat(f.saldo||0).toFixed(2) }));
              filas.push({ _codigo: '', _nombre: 'TOTAL PASIVO + PATRIMONIO', _activo: '', _pasivopatrimonio: +parseFloat(data.total_pasivo_patrimonio||0).toFixed(2) });
              exportStyledXlsx({
                empresa, titulo: 'BALANCE GENERAL', periodoLabel: `Hasta período ${fmtPeriodo(hasta)}`,
                columns, rows: filas, filename: `BalanceGeneral_${hasta}.xlsx`, sheetName: 'Balance General',
              });
            }}>Excel</Button>
            <Button variant="outlined" color="error" startIcon={<PictureAsPdfIcon />} onClick={() => {
              const rows = [];
              (data.activos||[]).forEach(f => rows.push([f.codigo, f.nombre, fmtNum(f.saldo), '']));
              rows.push(['','TOTAL ACTIVO',fmtNum(data.total_activo),'']);
              (data.pasivos||[]).forEach(f => rows.push([f.codigo, f.nombre, '', fmtNum(f.saldo)]));
              (data.patrimonio||[]).forEach(f => rows.push([f.codigo, f.nombre, '', fmtNum(f.saldo)]));
              rows.push(['','TOTAL PAS.+PAT.','',fmtNum(data.total_pasivo_patrimonio)]);
              exportPdf(`Balance General — ${empresa?.razon_social}`, `Hasta período ${fmtPeriodo(hasta)}`,
                ['Código','Cuenta','Activo','Pas.+Pat.'], rows, `BalanceGeneral_${hasta}.pdf`);
            }}>PDF</Button>
          </>)}
          {data && (
            <Chip
              icon={data.cuadra ? <CheckCircleIcon /> : <WarningIcon />}
              label={data.cuadra ? 'Balance cuadra ✓' : 'Balance no cuadra — revisar asientos'}
              color={data.cuadra ? 'success' : 'error'}
              variant="outlined"
            />
          )}
          {data && (
            <Chip label={`Expresado en ${moneda === 'USD' ? 'Dólares (US$)' : 'Soles (S/)'}`}
              color={moneda === 'USD' ? 'info' : 'default'} variant="outlined" sx={{ fontWeight: 'bold' }} />
          )}
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <Grid container spacing={3}>
          {/* Columna ACTIVO */}
          <Grid item xs={12} md={6}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">{empresa?.razon_social}</Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Balance General — hasta {fmtPeriodo(hasta)}
              </Typography>
            </Box>

            <GrupoBalance titulo="ACTIVO" filas={data.activos} total={data.total_activo} color="#0d47a1" />

            <Paper sx={{ p: 2, bgcolor: '#e3f2fd', border: '2px solid #0d47a1', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography fontWeight="bold" color="#0d47a1">TOTAL ACTIVO</Typography>
                <Typography fontFamily="monospace" fontWeight="bold" fontSize={16} color="#0d47a1">
                  {fmtNumSaldo(data.total_activo)}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Columna PASIVO + PATRIMONIO */}
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2, display: { xs: 'none', md: 'block' } }}>
              <Typography variant="h6" fontWeight="bold" sx={{ visibility: 'hidden' }}>x</Typography>
              <Typography variant="subtitle2" color="text.secondary" sx={{ visibility: 'hidden' }}>x</Typography>
            </Box>

            <GrupoBalance titulo="PASIVO" filas={data.pasivos} total={data.total_pasivo} color="#6a1b9a" />

            <GrupoBalance titulo="PATRIMONIO" filas={data.patrimonio} total={data.total_patrimonio} color="#2e7d32" />

            {/* Resultado del ejercicio (calculado) */}
            <Box sx={{ mb: 1.5 }}>
              <Box sx={{ bgcolor: esUtilidad ? '#1b5e20' : '#b71c1c', px: 2, py: 0.8, borderRadius: '4px 4px 0 0' }}>
                <Typography variant="subtitle2" fontWeight="bold" color="white">
                  RESULTADO DEL EJERCICIO (calculado)
                </Typography>
              </Box>
              <Paper variant="outlined" sx={{ borderTop: 0, borderRadius: '0 0 4px 4px', p: 1.5, px: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    {esUtilidad ? 'Utilidad del ejercicio' : 'Pérdida del ejercicio'}
                  </Typography>
                  <Typography fontFamily="monospace" fontWeight="bold"
                    color={esUtilidad ? '#2e7d32' : '#c62828'}>
                    {esUtilidad ? '' : '('}{fmtNumSaldo(Math.abs(data.resultado_ejercicio))}{esUtilidad ? '' : ')'}
                  </Typography>
                </Box>
              </Paper>
            </Box>

            <Paper sx={{ p: 2, bgcolor: '#f3e5f5', border: '2px solid #6a1b9a', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography fontWeight="bold" color="#6a1b9a">TOTAL PASIVO + PATRIMONIO</Typography>
                <Typography fontFamily="monospace" fontWeight="bold" fontSize={16} color="#6a1b9a">
                  {fmtNumSaldo(data.total_pasivo_patrimonio)}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {!loading && !data && hasta && (
        <Alert severity="info">No hay saldos en las cuentas de balance para el período seleccionado.</Alert>
      )}
    </Box>
  );
}

// ─── Ratios Financieros ──────────────────────────────────────────────────────

function fmtRatio(v, esPorcentaje = false) {
  if (v === null || v === undefined) return 'N/A';
  return esPorcentaje ? `${(v * 100).toFixed(1)}%` : v.toFixed(2);
}

function TarjetaRatio({ titulo, valor, esPorcentaje, descripcion, bueno }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', height: '100%' }}>
      <Typography variant="caption" color="text.secondary">{titulo}</Typography>
      <Typography variant="h5" fontWeight="bold" color={bueno === true ? 'success.main' : bueno === false ? 'error.main' : 'text.primary'}>
        {fmtRatio(valor, esPorcentaje)}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{descripcion}</Typography>
    </Paper>
  );
}

function RatiosFinancieros({ empresaId, empresa, periodos }) {
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingExcel, setLoadingExcel] = useState(false);
  const [error, setError] = useState('');

  const buscar = async () => {
    if (!desde || !hasta) { setError('Seleccione período desde y hasta'); return; }
    setLoading(true); setError(''); setData(null);
    try {
      const r = await axios.get('/api/contabilidad/reportes/ratios-financieros', {
        params: { empresa_id: empresaId, periodo_desde: desde, periodo_hasta: hasta }
      });
      setData(r.data);
    } catch (e) { setError(e.response?.data?.error || e.message); }
    setLoading(false);
  };

  return (
    <Box>
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField select label="Período desde" size="small" value={desde}
            onChange={e => setDesde(e.target.value)} sx={{ minWidth: 180 }}>
            {periodos.map(p => <MenuItem key={p.periodo} value={p.periodo}>{fmtPeriodo(p.periodo)}</MenuItem>)}
          </TextField>
          <TextField select label="Período hasta" size="small" value={hasta}
            onChange={e => setHasta(e.target.value)} sx={{ minWidth: 180 }}>
            {periodos.map(p => <MenuItem key={p.periodo} value={p.periodo}>{fmtPeriodo(p.periodo)}</MenuItem>)}
          </TextField>
          <Button variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            onClick={buscar} disabled={loading}>
            Generar
          </Button>
          {data && (<>
            <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}>Imprimir</Button>
            <Button variant="outlined" color="success" startIcon={loadingExcel ? <CircularProgress size={16} /> : <DownloadIcon />}
              disabled={loadingExcel} onClick={async () => {
                setLoadingExcel(true);
                try {
                  const r = await axios.get('/api/contabilidad/reportes/ratios-financieros/excel', {
                    params: { empresa_id: empresaId, periodo_desde: desde, periodo_hasta: hasta },
                    responseType: 'blob',
                  });
                  const url = window.URL.createObjectURL(new Blob([r.data]));
                  const a = document.createElement('a');
                  a.href = url; a.download = `RatiosFinancieros_${desde}_${hasta}.xlsx`; a.click();
                  window.URL.revokeObjectURL(url);
                } catch (e) { alert('Error al descargar el Excel'); }
                setLoadingExcel(false);
              }}>Excel</Button>
            <Button variant="outlined" color="error" startIcon={<PictureAsPdfIcon />} onClick={() => {
              const rows = [
                ['Razón corriente', fmtRatio(data.liquidez.razon_corriente)],
                ['Prueba ácida', fmtRatio(data.liquidez.prueba_acida)],
                ['Capital de trabajo', fmtNum(data.liquidez.capital_trabajo)],
                ['Endeudamiento del activo', fmtRatio(data.solvencia.endeudamiento_activo, true)],
                ['Endeudamiento patrimonial', fmtRatio(data.solvencia.endeudamiento_patrimonial, true)],
                ['Razón de autonomía', fmtRatio(data.solvencia.razon_autonomia, true)],
                ['Margen bruto', fmtRatio(data.rentabilidad.margen_bruto, true)],
                ['Margen neto', fmtRatio(data.rentabilidad.margen_neto, true)],
                ['ROA', fmtRatio(data.rentabilidad.roa, true)],
                ['ROE', fmtRatio(data.rentabilidad.roe, true)],
              ];
              exportPdf(`Ratios Financieros — ${empresa?.razon_social}`, `${fmtPeriodo(desde)} a ${fmtPeriodo(hasta)}`,
                ['Ratio', 'Valor'], rows, `RatiosFinancieros_${desde}_${hasta}.pdf`);
            }}>PDF</Button>
          </>)}
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            Activo corriente / no corriente y Pasivo corriente se estiman por rango de código de cuenta
            (10-29 corriente, 30-39 no corriente para activo; 40-49 corriente para pasivo). Es una aproximación
            práctica, no una clasificación contable exacta por cuenta.
          </Alert>

          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Liquidez</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <TarjetaRatio titulo="Razón Corriente" valor={data.liquidez.razon_corriente}
                descripcion="Activo Corriente / Pasivo Corriente" bueno={data.liquidez.razon_corriente >= 1} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TarjetaRatio titulo="Prueba Ácida" valor={data.liquidez.prueba_acida}
                descripcion="(Act. Corriente - Existencias) / Pas. Corriente" bueno={data.liquidez.prueba_acida >= 1} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TarjetaRatio titulo="Capital de Trabajo" valor={data.liquidez.capital_trabajo}
                descripcion="Activo Corriente − Pasivo Corriente" bueno={data.liquidez.capital_trabajo >= 0} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Solvencia / Endeudamiento</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <TarjetaRatio titulo="Endeudamiento del Activo" valor={data.solvencia.endeudamiento_activo} esPorcentaje
                descripcion="Pasivo Total / Activo Total" bueno={data.solvencia.endeudamiento_activo <= 0.6} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TarjetaRatio titulo="Endeudamiento Patrimonial" valor={data.solvencia.endeudamiento_patrimonial} esPorcentaje
                descripcion="Pasivo Total / Patrimonio" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TarjetaRatio titulo="Razón de Autonomía" valor={data.solvencia.razon_autonomia} esPorcentaje
                descripcion="Patrimonio / Activo Total" bueno={data.solvencia.razon_autonomia >= 0.4} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Rentabilidad</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <TarjetaRatio titulo="Margen Bruto" valor={data.rentabilidad.margen_bruto} esPorcentaje
                descripcion="Utilidad Bruta / Ventas" bueno={data.rentabilidad.margen_bruto >= 0} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TarjetaRatio titulo="Margen Neto" valor={data.rentabilidad.margen_neto} esPorcentaje
                descripcion="Utilidad Neta / Ventas" bueno={data.rentabilidad.margen_neto >= 0} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TarjetaRatio titulo="ROA" valor={data.rentabilidad.roa} esPorcentaje
                descripcion="Rentabilidad sobre Activos" bueno={data.rentabilidad.roa >= 0} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TarjetaRatio titulo="ROE" valor={data.rentabilidad.roe} esPorcentaje
                descripcion="Rentabilidad sobre Patrimonio" bueno={data.rentabilidad.roe >= 0} />
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}

// ─── Análisis Vertical y Horizontal ─────────────────────────────────────────

function fmtVar(v) {
  if (v === null || v === undefined) return '—';
  const n = parseFloat(v);
  return (n >= 0 ? '+' : '') + n.toFixed(1) + '%';
}

function FilaAnalisisEV({ r }) {
  return (
    <TableRow hover>
      <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#388e3c', fontSize: 12 }}>{r.codigo}</TableCell>
      <TableCell sx={{ fontSize: 12 }}>{r.descripcion}</TableCell>
      <TableCell align="right">{fmtNum(r.saldo_actual)}</TableCell>
      <TableCell align="right" sx={{ color: 'text.secondary' }}>{r.pct_actual.toFixed(1)}%</TableCell>
      <TableCell align="right">{fmtNum(r.saldo_anterior)}</TableCell>
      <TableCell align="right" sx={{ color: 'text.secondary' }}>{r.pct_anterior.toFixed(1)}%</TableCell>
      <TableCell align="right" sx={{ color: r.variacion_abs >= 0 ? 'success.main' : 'error.main' }}>{fmtNum(r.variacion_abs)}</TableCell>
      <TableCell align="right" sx={{ color: r.variacion_abs >= 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}>{fmtVar(r.variacion_pct)}</TableCell>
    </TableRow>
  );
}

function TablaAnalisisEV({ titulo, filas, totalActual, totalAnterior, labelTotal }) {
  const varTotal = variacionLocal(totalActual, totalAnterior);
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>{titulo}</Typography>
      <TableContainer component={Paper} elevation={2} sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 90 }}>Código</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Cuenta</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Actual</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">% Vert.</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Anterior</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">% Vert.</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Var. Abs.</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Var. %</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filas.map((r, i) => <FilaAnalisisEV key={i} r={r} />)}
            <TableRow sx={{ bgcolor: '#FFF8DC' }}>
              <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>{labelTotal}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{fmtNum(totalActual)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>100.0%</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{fmtNum(totalAnterior)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>100.0%</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{fmtNum(varTotal.variacion_abs)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{fmtVar(varTotal.variacion_pct)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function variacionLocal(actual, anterior) {
  const abs = actual - anterior;
  const pct = anterior !== 0 ? (abs / Math.abs(anterior)) * 100 : (actual !== 0 ? null : 0);
  return { variacion_abs: abs, variacion_pct: pct };
}

function AnalisisVerticalHorizontal({ empresaId, empresa, periodos }) {
  const [tipo, setTipo] = useState('balance');
  const [actual, setActual] = useState('');
  const [anterior, setAnterior] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const buscar = async () => {
    if (!actual || !anterior) { setError('Seleccione el período actual y el período de comparación'); return; }
    setLoading(true); setError(''); setData(null);
    try {
      if (tipo === 'balance') {
        const r = await axios.get('/api/contabilidad/reportes/analisis-ev-balance', {
          params: { empresa_id: empresaId, periodo_actual: actual, periodo_anterior: anterior }
        });
        setData(r.data);
      } else {
        const r = await axios.get('/api/contabilidad/reportes/analisis-ev-resultados', {
          params: {
            empresa_id: empresaId,
            periodo_desde_actual: actual, periodo_hasta_actual: actual,
            periodo_desde_anterior: anterior, periodo_hasta_anterior: anterior,
          }
        });
        setData(r.data);
      }
    } catch (e) { setError(e.response?.data?.error || e.message); }
    setLoading(false);
  };

  const columnasExcel = [
    { header: 'Código', key: '_codigo', width: 12 },
    { header: 'Cuenta', key: '_desc', width: 42 },
    { header: 'Actual', key: '_actual', width: 16, type: 'number' },
    { header: '% Vert. Actual', key: '_pctActual', width: 14, type: 'number' },
    { header: 'Anterior', key: '_anterior', width: 16, type: 'number' },
    { header: '% Vert. Anterior', key: '_pctAnterior', width: 14, type: 'number' },
    { header: 'Variación Abs.', key: '_varAbs', width: 16, type: 'number' },
    { header: 'Variación %', key: '_varPct', width: 14, type: 'number' },
  ];
  const filaExcel = (r) => ({
    _codigo: r.codigo, _desc: r.descripcion,
    _actual: +r.saldo_actual.toFixed(2), _pctActual: +r.pct_actual.toFixed(1),
    _anterior: +r.saldo_anterior.toFixed(2), _pctAnterior: +r.pct_anterior.toFixed(1),
    _varAbs: +r.variacion_abs.toFixed(2), _varPct: r.variacion_pct === null ? '' : +r.variacion_pct.toFixed(1),
  });

  const descargarExcel = () => {
    if (!data) return;
    let filas = [];
    if (tipo === 'balance') {
      filas = [
        ...data.activos.map(filaExcel),
        { _codigo: '', _desc: 'TOTAL ACTIVO', _actual: +data.total_activo_actual.toFixed(2), _pctActual: 100, _anterior: +data.total_activo_anterior.toFixed(2), _pctAnterior: 100, _varAbs: '', _varPct: '' },
        ...data.pasivos.map(filaExcel),
        ...data.patrimonio.map(filaExcel),
        { _codigo: '', _desc: 'TOTAL PASIVO + PATRIMONIO', _actual: +data.total_pp_actual.toFixed(2), _pctActual: 100, _anterior: +data.total_pp_anterior.toFixed(2), _pctAnterior: 100, _varAbs: '', _varPct: '' },
      ];
    } else {
      filas = [
        ...data.ingresos.map(filaExcel),
        { _codigo: '', _desc: 'TOTAL INGRESOS', _actual: +data.total_ingresos_actual.toFixed(2), _pctActual: 100, _anterior: +data.total_ingresos_anterior.toFixed(2), _pctAnterior: 100, _varAbs: '', _varPct: '' },
        ...data.gastos.map(filaExcel),
        { _codigo: '', _desc: 'TOTAL GASTOS', _actual: +data.total_gastos_actual.toFixed(2), _pctActual: '', _anterior: +data.total_gastos_anterior.toFixed(2), _pctAnterior: '', _varAbs: '', _varPct: '' },
        { _codigo: '', _desc: 'RESULTADO NETO', _actual: +data.resultado_neto_actual.toFixed(2), _pctActual: '', _anterior: +data.resultado_neto_anterior.toFixed(2), _pctAnterior: '', _varAbs: +data.variacion_abs.toFixed(2), _varPct: data.variacion_pct === null ? '' : +data.variacion_pct.toFixed(1) },
      ];
    }
    exportStyledXlsx({
      empresa, titulo: tipo === 'balance' ? 'ANÁLISIS VERTICAL Y HORIZONTAL — BALANCE GENERAL' : 'ANÁLISIS VERTICAL Y HORIZONTAL — ESTADO DE RESULTADOS',
      periodoLabel: `${fmtPeriodo(actual)} vs ${fmtPeriodo(anterior)}`,
      columns: columnasExcel, rows: filas, landscape: true,
      filename: `AnalisisEV_${tipo}_${actual}_vs_${anterior}.xlsx`, sheetName: 'Análisis EV',
    });
  };

  return (
    <Box>
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField select label="Estado" size="small" value={tipo}
            onChange={e => { setTipo(e.target.value); setData(null); }} sx={{ minWidth: 190 }}>
            <MenuItem value="balance">Balance General</MenuItem>
            <MenuItem value="resultados">Estado de Resultados</MenuItem>
          </TextField>
          <TextField select label="Período actual" size="small" value={actual}
            onChange={e => setActual(e.target.value)} sx={{ minWidth: 180 }}>
            {periodos.map(p => <MenuItem key={p.periodo} value={p.periodo}>{fmtPeriodo(p.periodo)}</MenuItem>)}
          </TextField>
          <TextField select label="Período de comparación" size="small" value={anterior}
            onChange={e => setAnterior(e.target.value)} sx={{ minWidth: 200 }}>
            {periodos.map(p => <MenuItem key={p.periodo} value={p.periodo}>{fmtPeriodo(p.periodo)}</MenuItem>)}
          </TextField>
          <Button variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            onClick={buscar} disabled={loading}>
            Generar
          </Button>
          {data && (<>
            <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}>Imprimir</Button>
            <Button variant="outlined" color="success" startIcon={<DownloadIcon />} onClick={descargarExcel}>Excel</Button>
          </>)}
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && tipo === 'balance' && (
        <>
          <TablaAnalisisEV titulo="Activo" filas={data.activos}
            totalActual={data.total_activo_actual} totalAnterior={data.total_activo_anterior} labelTotal="TOTAL ACTIVO" />
          <TablaAnalisisEV titulo="Pasivo" filas={data.pasivos}
            totalActual={data.pasivos.reduce((s,r)=>s+r.saldo_actual,0)} totalAnterior={data.pasivos.reduce((s,r)=>s+r.saldo_anterior,0)} labelTotal="TOTAL PASIVO" />
          <TablaAnalisisEV titulo="Patrimonio (incluye resultado del ejercicio)" filas={data.patrimonio}
            totalActual={data.total_pp_actual - data.pasivos.reduce((s,r)=>s+r.saldo_actual,0)}
            totalAnterior={data.total_pp_anterior - data.pasivos.reduce((s,r)=>s+r.saldo_anterior,0)} labelTotal="TOTAL PATRIMONIO" />
        </>
      )}

      {data && tipo === 'resultados' && (
        <>
          <TablaAnalisisEV titulo="Ingresos" filas={data.ingresos}
            totalActual={data.total_ingresos_actual} totalAnterior={data.total_ingresos_anterior} labelTotal="TOTAL INGRESOS" />
          <TablaAnalisisEV titulo="Gastos y Costos (% sobre Ingresos)" filas={data.gastos}
            totalActual={data.total_gastos_actual} totalAnterior={data.total_gastos_anterior} labelTotal="TOTAL GASTOS" />
          <Paper elevation={1} sx={{ p: 2, bgcolor: data.resultado_neto_actual >= 0 ? '#e8f5e9' : '#ffebee' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Typography fontWeight="bold">Resultado Neto</Typography>
              <Typography>Actual: <b>{fmtNum(data.resultado_neto_actual)}</b></Typography>
              <Typography>Anterior: <b>{fmtNum(data.resultado_neto_anterior)}</b></Typography>
              <Typography sx={{ color: data.variacion_abs >= 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}>
                Variación: {fmtNum(data.variacion_abs)} ({fmtVar(data.variacion_pct)})
              </Typography>
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
}

// ─── Flujo de Caja Proyectado ───────────────────────────────────────────────

function FlujoCajaProyectado({ empresaId, empresa }) {
  const [horizonte, setHorizonte] = useState(60);
  const [agrupacion, setAgrupacion] = useState('semanal');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const buscar = async () => {
    setLoading(true); setError(''); setData(null);
    try {
      const r = await axios.get('/api/contabilidad/reportes/flujo-caja-proyectado', {
        params: { empresa_id: empresaId, horizonte_dias: horizonte, agrupacion }
      });
      setData(r.data);
    } catch (e) { setError(e.response?.data?.error || e.message); }
    setLoading(false);
  };

  const descargarExcel = () => {
    if (!data) return;
    const columns = [
      { header: 'Período', key: '_periodo', width: 24 },
      { header: 'Cobros Proyectados', key: '_cobros', width: 18, type: 'number' },
      { header: 'Pagos Proyectados', key: '_pagos', width: 18, type: 'number' },
      { header: 'Flujo Neto', key: '_neto', width: 16, type: 'number' },
      { header: 'Saldo Acumulado', key: '_saldo', width: 18, type: 'number' },
    ];
    const filas = [
      { _periodo: `Saldo inicial de caja al ${data.fecha_corte}`, _cobros: '', _pagos: '', _neto: '', _saldo: data.saldo_caja_inicial },
      { _periodo: 'Vencido a la fecha (exigible de inmediato)', _cobros: data.vencido_cxc, _pagos: data.vencido_cxp, _neto: +(data.vencido_cxc - data.vencido_cxp).toFixed(2), _saldo: '' },
      ...data.buckets.map(b => ({ _periodo: `${b.desde} a ${b.hasta}`, _cobros: b.cobros, _pagos: b.pagos, _neto: b.neto, _saldo: b.saldo_acumulado })),
      { _periodo: 'Más allá del horizonte (no proyectado)', _cobros: data.fuera_horizonte_cxc, _pagos: data.fuera_horizonte_cxp, _neto: '', _saldo: '' },
    ];
    exportStyledXlsx({
      empresa, titulo: 'FLUJO DE CAJA PROYECTADO', periodoLabel: `Desde ${data.fecha_corte} — horizonte ${data.horizonte_dias} días (${data.agrupacion})`,
      columns, rows: filas, filename: `FlujoCajaProyectado_${data.fecha_corte}.xlsx`, sheetName: 'Flujo de Caja Proyectado',
    });
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Proyección hacia adelante a partir del saldo actual de Caja y Bancos (cuentas 10x) y los documentos de
        Cuentas por Cobrar / por Pagar con saldo pendiente, usando su fecha de vencimiento. No es el Estado de
        Flujos de Efectivo (que es histórico) — es una estimación de liquidez futura, útil para planificar pagos y cobranzas.
      </Alert>
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField select label="Horizonte" size="small" value={horizonte}
            onChange={e => setHorizonte(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value={30}>30 días</MenuItem>
            <MenuItem value={60}>60 días</MenuItem>
            <MenuItem value={90}>90 días</MenuItem>
            <MenuItem value={180}>180 días</MenuItem>
          </TextField>
          <TextField select label="Agrupación" size="small" value={agrupacion}
            onChange={e => setAgrupacion(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="semanal">Semanal</MenuItem>
            <MenuItem value="mensual">Mensual (~30d)</MenuItem>
          </TextField>
          <Button variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            onClick={buscar} disabled={loading}>
            Generar
          </Button>
          {data && (<>
            <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}>Imprimir</Button>
            <Button variant="outlined" color="success" startIcon={<DownloadIcon />} onClick={descargarExcel}>Excel</Button>
          </>)}
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <>
          {data.hay_fechas_estimadas && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Algunos documentos no tienen fecha de vencimiento registrada — se les asumió 30 días desde su fecha
              de emisión. Para una proyección más exacta, registre la fecha de vencimiento real al crear el asiento.
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">Saldo de Caja Actual</Typography>
                <Typography variant="h5" fontWeight="bold">{fmtNum(data.saldo_caja_inicial)}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">Vencido por Cobrar (exigible ya)</Typography>
                <Typography variant="h5" fontWeight="bold" color="success.main">{fmtNum(data.vencido_cxc)}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">Vencido por Pagar (exigible ya)</Typography>
                <Typography variant="h5" fontWeight="bold" color="error.main">{fmtNum(data.vencido_cxp)}</Typography>
              </Paper>
            </Grid>
          </Grid>

          <TableContainer component={Paper} elevation={2} sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Período</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Cobros Proy.</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Pagos Proy.</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Flujo Neto</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Saldo Acumulado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.buckets.map((b, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ fontSize: 12 }}>{b.desde} a {b.hasta}</TableCell>
                    <TableCell align="right" sx={{ color: 'success.main' }}>{fmtNum(b.cobros)}</TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>{fmtNum(b.pagos)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: b.neto >= 0 ? 'success.main' : 'error.main' }}>{fmtNum(b.neto)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: b.saldo_acumulado >= 0 ? 'text.primary' : 'error.main' }}>{fmtNum(b.saldo_acumulado)}</TableCell>
                  </TableRow>
                ))}
                {(data.fuera_horizonte_cxc > 0 || data.fuera_horizonte_cxp > 0) && (
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontStyle: 'italic', fontSize: 12 }}>Más allá del horizonte (no proyectado)</TableCell>
                    <TableCell align="right">{fmtNum(data.fuera_horizonte_cxc)}</TableCell>
                    <TableCell align="right">{fmtNum(data.fuera_horizonte_cxp)}</TableCell>
                    <TableCell align="right">—</TableCell>
                    <TableCell align="right">—</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}

// ─── Conciliación Tributaria (papeles de trabajo) ───────────────────────────

function ConciliacionTributaria({ empresaId, empresa }) {
  const anioActual = new Date().getFullYear();
  const [anio, setAnio] = useState(anioActual);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({ tipo: 'ADICION', concepto: '', monto: '' });
  const [saving, setSaving] = useState(false);

  const cargar = useCallback(async () => {
    if (!empresaId || !anio) return;
    setLoading(true); setError('');
    try {
      const r = await axios.get('/api/contabilidad/conciliacion-tributaria', { params: { empresa_id: empresaId, anio } });
      setData(r.data);
    } catch (e) { setError(e.response?.data?.error || e.message); }
    setLoading(false);
  }, [empresaId, anio]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirNuevo = () => { setEditando(null); setForm({ tipo: 'ADICION', concepto: '', monto: '' }); setDlgOpen(true); };
  const abrirEditar = (item) => { setEditando(item); setForm({ tipo: item.tipo, concepto: item.concepto, monto: item.monto }); setDlgOpen(true); };

  const guardar = async () => {
    if (!form.concepto || form.monto === '') return;
    setSaving(true);
    try {
      if (editando) await axios.put(`/api/contabilidad/conciliacion-tributaria/${editando.id}`, form);
      else await axios.post('/api/contabilidad/conciliacion-tributaria', { ...form, empresa_id: empresaId, anio });
      setDlgOpen(false); cargar();
    } catch (e) { setError(e.response?.data?.error || e.message); }
    setSaving(false);
  };

  const eliminar = async (item) => {
    if (!window.confirm(`¿Eliminar "${item.concepto}"?`)) return;
    try { await axios.delete(`/api/contabilidad/conciliacion-tributaria/${item.id}`); cargar(); }
    catch (e) { setError(e.response?.data?.error || e.message); }
  };

  const descargarExcel = async () => {
    try {
      const r = await axios.get('/api/contabilidad/conciliacion-tributaria/excel', { params: { empresa_id: empresaId, anio }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `ConciliacionTributaria_${anio}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert('Error al descargar el Excel'); }
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Papeles de trabajo internos para llegar de la Utilidad Contable a la Renta Imponible. No presenta nada a SUNAT
        (eso se hace con el Formulario Virtual 710 en su portal) ni calcula el Impuesto a la Renta, porque la tasa
        depende del régimen de cada empresa (Régimen General 29.5% vs RMT con tasas escalonadas).
      </Alert>
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField select label="Ejercicio" size="small" value={anio} onChange={e => setAnio(e.target.value)} sx={{ minWidth: 140 }}>
            {[anioActual, anioActual - 1, anioActual - 2].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </TextField>
          <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={abrirNuevo} disabled={!empresaId}>
            Nueva Adición/Deducción
          </Button>
          {data && (
            <Button variant="outlined" color="success" startIcon={<DownloadIcon />} onClick={descargarExcel}>Excel</Button>
          )}
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <CircularProgress size={24} />}

      {data && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">Utilidad (Pérdida) Contable</Typography>
                <Typography variant="h5" fontWeight="bold" color={data.utilidad_contable >= 0 ? 'success.main' : 'error.main'}>{fmtNum(data.utilidad_contable)}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">Adiciones (+) / Deducciones (−)</Typography>
                <Typography variant="h6" fontWeight="bold">+{fmtNum(data.total_adiciones)} / −{fmtNum(data.total_deducciones)}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper elevation={2} sx={{ p: 2, bgcolor: '#FFF8DC' }}>
                <Typography variant="body2" color="text.secondary">Renta Imponible Estimada</Typography>
                <Typography variant="h5" fontWeight="bold">{fmtNum(data.renta_imponible)}</Typography>
              </Paper>
            </Grid>
          </Grid>

          <TableContainer component={Paper} elevation={2}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Concepto</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Tipo</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Monto</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Acc.</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.items.length === 0 ? (
                  <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>Sin adiciones ni deducciones registradas para {anio}</TableCell></TableRow>
                ) : data.items.map(item => (
                  <TableRow key={item.id} hover>
                    <TableCell sx={{ fontSize: 13 }}>{item.concepto}</TableCell>
                    <TableCell align="center">
                      <Chip size="small" label={item.tipo === 'ADICION' ? 'Adición' : 'Deducción'} color={item.tipo === 'ADICION' ? 'warning' : 'info'} />
                    </TableCell>
                    <TableCell align="right">{fmtNum(item.monto)}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => abrirEditar(item)}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => eliminar(item)}><DeleteIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editando ? 'Editar' : 'Nueva'} Adición/Deducción</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField select label="Tipo" size="small" value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}>
              <MenuItem value="ADICION">Adición (suma a la utilidad)</MenuItem>
              <MenuItem value="DEDUCCION">Deducción (resta de la utilidad)</MenuItem>
            </TextField>
            <TextField label="Concepto" size="small" value={form.concepto} onChange={e => setForm(f => ({ ...f, concepto: e.target.value }))}
              placeholder="Ej: Gastos no sustentados con comprobante de pago" multiline rows={2} />
            <TextField label="Monto S/" size="small" type="number" value={form.monto} onChange={e => setForm(f => ({ ...f, monto: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={guardar} disabled={saving}>{saving ? <CircularProgress size={18} /> : 'Guardar'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Libro Caja y Bancos ─────────────────────────────────────────────────────

const TIPOS_SUNAT_MAP = {
  '01': 'Factura', '03': 'Boleta', '07': 'N. Crédito', '08': 'N. Débito',
  '12': 'Ticket', '14': 'Rec. Serv.', '18': 'Doc. Banco',
};

function LibroCajaBancos({ empresaId, empresa, periodos }) {
  const [periodo, setPeriodo] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [moneda, setMoneda] = useState('PEN');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!periodos.length) return;
    const abiertos = periodos.filter(p => p.estado === 'ABIERTO').sort((a, b) => b.periodo.localeCompare(a.periodo));
    if (abiertos.length > 0) {
      const p = abiertos[0].periodo;
      setPeriodo(p);
    }
    setData(null);
  }, [periodos, empresaId]);

  const buscar = async () => {
    setLoading(true); setError(''); setData(null);
    try {
      const params = { empresa_id: empresaId, moneda };
      if (periodo)     params.periodo     = periodo;
      if (fechaDesde)  params.fecha_desde = fechaDesde;
      if (fechaHasta)  params.fecha_hasta = fechaHasta;
      const r = await axios.get('/api/contabilidad/reportes/libro-caja-bancos', { params });
      setData(r.data);
    } catch (e) { setError(e.response?.data?.error || e.message); }
    setLoading(false);
  };

  const exportarExcel = () => {
    if (!data?.cuentas?.length) return;
    const wb = XLSX.utils.book_new();
    for (const cuenta of data.cuentas) {
      const hdrs = ['Fecha','Tipo-Nro','Glosa','Doc. Referencia','Entradas','Salidas','Saldo'];
      const rows = cuenta.movimientos.map(m => [
        m.fecha,
        `${m.tipo_documento}-${m.numero}`,
        m.glosa + (m.glosa_det ? ` / ${m.glosa_det}` : '') + (m.auxiliar_nombre ? ` [${m.auxiliar_nombre}]` : ''),
        m.doc_ref_tipo ? `${TIPOS_SUNAT_MAP[m.doc_ref_tipo]||m.doc_ref_tipo} ${m.doc_ref_serie}-${m.doc_ref_numero}` : '',
        parseFloat(m.debe || 0),
        parseFloat(m.haber || 0),
        parseFloat(m.saldo || 0),
      ]);
      rows.push(['','','','TOTALES', cuenta.total_entradas, cuenta.total_salidas, cuenta.saldo]);
      const ws = XLSX.utils.aoa_to_sheet([hdrs, ...rows]);
      ws['!cols'] = [12,16,40,22,14,14,14].map(w => ({ wch: w }));
      XLSX.utils.book_append_sheet(wb, ws, cuenta.codigo.substring(0,31));
    }
    const label = periodo || `${fechaDesde}_${fechaHasta}`;
    XLSX.writeFile(wb, `LibroCajaBancos_${label}.xlsx`);
  };

  const exportarPdf = () => {
    if (!data?.cuentas?.length) return;
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    let y = 14;
    doc.setFontSize(12);
    doc.text(`Libro Caja y Bancos — ${empresa?.razon_social || ''}`, 14, y); y += 7;
    doc.setFontSize(9);
    doc.text(periodo ? `Período: ${fmtPeriodo(periodo)}` : `${fechaDesde} al ${fechaHasta}`, 14, y); y += 5;
    for (const cuenta of data.cuentas) {
      if (y > 160) { doc.addPage(); y = 14; }
      doc.setFontSize(10); doc.setFont(undefined, 'bold');
      doc.text(`${cuenta.codigo} — ${cuenta.descripcion}`, 14, y); y += 5;
      doc.setFont(undefined, 'normal');
      autoTable(doc, {
        startY: y,
        head: [['Fecha','Tipo-Nro','Glosa / Auxiliar','Doc. Referencia','Entradas','Salidas','Saldo']],
        body: [
          ...cuenta.movimientos.map(m => [
            m.fecha,
            `${m.tipo_documento}-${m.numero}`,
            (m.glosa + (m.auxiliar_nombre ? ` [${m.auxiliar_nombre}]` : '')).substring(0, 45),
            m.doc_ref_tipo ? `${m.doc_ref_serie||''}-${m.doc_ref_numero||''}` : '',
            m.debe > 0 ? fmtNum(m.debe) : '',
            m.haber > 0 ? fmtNum(m.haber) : '',
            fmtNumSaldo(m.saldo),
          ]),
          ['','','','TOTALES', fmtNum(cuenta.total_entradas), fmtNum(cuenta.total_salidas), fmtNumSaldo(cuenta.saldo)],
        ],
        styles: { fontSize: 7 },
        headStyles: { fillColor: cuenta.codigo.startsWith('10') ? [230,126,34] : [41,128,185] },
        columnStyles: {
          0:{cellWidth:20}, 1:{cellWidth:20}, 2:{cellWidth:58}, 3:{cellWidth:28},
          4:{halign:'right',cellWidth:22}, 5:{halign:'right',cellWidth:22}, 6:{halign:'right',cellWidth:22}
        },
        didParseCell: h => {
          if (h.row.index === cuenta.movimientos.length) h.cell.styles.fontStyle = 'bold';
        }
      });
      y = doc.lastAutoTable.finalY + 6;
    }
    doc.save(`LibroCajaBancos_${periodo || fechaDesde}.pdf`);
  };

  const totalEntradas = (data?.cuentas || []).reduce((s, c) => s + c.total_entradas, 0);
  const totalSalidas  = (data?.cuentas || []).reduce((s, c) => s + c.total_salidas, 0);
  const saldoNeto     = (data?.cuentas || []).reduce((s, c) => s + c.saldo, 0);

  return (
    <Box>
      {/* Filtros */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField select label="Período" size="small" value={periodo}
            onChange={e => { setPeriodo(e.target.value); if (e.target.value) { setFechaDesde(''); setFechaHasta(''); } }}
            sx={{ minWidth: 170 }}>
            <MenuItem value="">— Por rango de fecha —</MenuItem>
            {periodos.map(p => <MenuItem key={p.periodo} value={p.periodo}>{fmtPeriodo(p.periodo)}</MenuItem>)}
          </TextField>
          {!periodo && <>
            <TextField size="small" label="Fecha Desde" type="date" value={fechaDesde}
              onChange={e => setFechaDesde(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 155 }} />
            <TextField size="small" label="Fecha Hasta" type="date" value={fechaHasta}
              onChange={e => setFechaHasta(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 155 }} />
          </>}
          <TextField select label="Moneda" size="small" value={moneda}
            onChange={e => setMoneda(e.target.value)} sx={{ minWidth: 130 }}>
            <MenuItem value="PEN">S/ Soles</MenuItem>
            <MenuItem value="USD">$ Dólares</MenuItem>
          </TextField>
          <Button variant="contained" color="warning"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            onClick={buscar} disabled={loading}>
            Generar
          </Button>
          {data && data.cuentas.length > 0 && <>
            <Button variant="outlined" color="success" size="small" startIcon={<DownloadIcon />} onClick={exportarExcel}>Excel</Button>
            <Button variant="outlined" color="error" size="small" startIcon={<PictureAsPdfIcon />} onClick={exportarPdf}>PDF</Button>
          </>}
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

      {!loading && data && (<>
        <Typography variant="body2" align="center" sx={{ mb: 1, fontWeight: 'bold', color: moneda === 'USD' ? 'info.main' : 'text.secondary' }}>
          Expresado en {moneda === 'USD' ? 'Dólares (US$)' : 'Soles (S/)'}
        </Typography>
        {/* Resumen totales */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          {[
            { label: 'Total Entradas', val: totalEntradas, color: '#e65100' },
            { label: 'Total Salidas',  val: totalSalidas,  color: '#1565c0' },
            { label: 'Saldo Neto',     val: saldoNeto,     color: saldoNeto >= 0 ? '#2e7d32' : '#c62828', big: true },
          ].map(c => (
            <Paper key={c.label} variant="outlined"
              sx={{ p: 1.5, minWidth: 160, flex: '1 1 160px', textAlign: 'center',
                borderTop: `4px solid ${c.color}`, borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block">{c.label}</Typography>
              <Typography variant={c.big ? 'h6' : 'subtitle1'} fontWeight="bold"
                sx={{ color: c.color, fontFamily: 'monospace' }}>
                {moneda === 'USD' ? '$' : 'S/'} {fmtNumSaldo(Math.abs(c.val))}
              </Typography>
            </Paper>
          ))}
        </Box>

        {data.cuentas.length === 0 ? (
          <Alert severity="info">No hay movimientos en cuentas de Caja (10x) ni Bancos (11x) para el período seleccionado.</Alert>
        ) : data.cuentas.map(cuenta => (
          <Box key={cuenta.codigo} sx={{ mb: 3 }}>
            {/* Encabezado cuenta */}
            <Box sx={{
              bgcolor: cuenta.codigo.startsWith('10') ? '#e65100' : '#1565c0',
              px: 2, py: 1, borderRadius: '6px 6px 0 0',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <Typography fontWeight="bold" color="white" sx={{ fontFamily: 'monospace' }}>
                {cuenta.codigo} — {cuenta.descripcion}
              </Typography>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Typography variant="caption" color="rgba(255,255,255,0.8)">
                  Entradas: <strong>{fmtNumSaldo(cuenta.total_entradas)}</strong>
                </Typography>
                <Typography variant="caption" color="rgba(255,255,255,0.8)">
                  Salidas: <strong>{fmtNumSaldo(cuenta.total_salidas)}</strong>
                </Typography>
                <Typography variant="caption" color="white" fontWeight="bold">
                  Saldo: {fmtNumSaldo(cuenta.saldo)}
                </Typography>
              </Box>
            </Box>
            <TableContainer component={Paper} variant="outlined"
              sx={{ borderTop: 0, borderRadius: '0 0 6px 6px', overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    {['Fecha','Tipo-Nro','Glosa / Auxiliar','Doc. que cancela','Entradas','Salidas','Saldo'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 'bold', fontSize: 11, py: 0.75,
                        ...(['Entradas','Salidas','Saldo'].includes(h) ? { textAlign: 'right', minWidth: 100 } : {}) }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cuenta.movimientos.map((m, i) => (
                    <TableRow key={i} hover sx={{ '&:nth-of-type(even)': { bgcolor: 'grey.50' } }}>
                      <TableCell sx={{ fontSize: 11, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{m.fecha}</TableCell>
                      <TableCell sx={{ fontSize: 11, fontFamily: 'monospace', fontWeight: 'bold' }}>
                        {m.tipo_documento}-{m.numero}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, maxWidth: 280 }}>
                        {m.glosa}
                        {m.glosa_det && <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>/ {m.glosa_det}</Typography>}
                        {m.auxiliar_nombre && (
                          <Typography variant="caption" display="block" sx={{ color: '#1565c0', fontStyle: 'italic' }}>
                            {m.auxiliar_nombre}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, fontFamily: 'monospace', color: '#6a1b9a' }}>
                        {m.doc_ref_tipo ? (
                          <Tooltip title={`${TIPOS_SUNAT_MAP[m.doc_ref_tipo] || m.doc_ref_tipo}`}>
                            <span>{m.doc_ref_tipo} {m.doc_ref_serie}{m.doc_ref_serie ? '-' : ''}{m.doc_ref_numero}</span>
                          </Tooltip>
                        ) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 11, fontFamily: 'monospace', color: '#e65100', fontWeight: m.debe > 0 ? 600 : 400 }}>
                        {m.debe > 0 ? fmtNumSaldo(m.debe) : ''}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 11, fontFamily: 'monospace', color: '#1565c0', fontWeight: m.haber > 0 ? 600 : 400 }}>
                        {m.haber > 0 ? fmtNumSaldo(m.haber) : ''}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 12, fontFamily: 'monospace', fontWeight: 'bold',
                        color: m.saldo >= 0 ? '#2e7d32' : '#c62828' }}>
                        {fmtNumSaldo(Math.abs(m.saldo))}
                        <Typography variant="caption" sx={{ ml: 0.3 }}>{m.saldo >= 0 ? 'D' : 'A'}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: cuenta.codigo.startsWith('10') ? '#fff3e0' : '#e3f2fd' }}>
                    <TableCell colSpan={4} align="right" sx={{ fontWeight: 'bold', fontSize: 12 }}>TOTALES</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'monospace', color: '#e65100' }}>
                      {fmtNumSaldo(cuenta.total_entradas)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'monospace', color: '#1565c0' }}>
                      {fmtNumSaldo(cuenta.total_salidas)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'monospace', fontSize: 13,
                      color: cuenta.saldo >= 0 ? '#2e7d32' : '#c62828' }}>
                      {fmtNumSaldo(Math.abs(cuenta.saldo))}
                      <Typography variant="caption" sx={{ ml: 0.3 }}>{cuenta.saldo >= 0 ? 'D' : 'A'}</Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))}
      </>)}

      {!loading && !data && (
        <Alert severity="info">Seleccione un período o rango de fechas y haga clic en Generar.</Alert>
      )}
    </Box>
  );
}
